<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import cltApi from 'dashboard/api/cltSimulador';
import simuladorApi from 'dashboard/api/simuladorApi';

// ============================================================================
// v92: SIMULADOR CLT dentro do Chatwoot — front novo, backend = o DIGITADOR
// de producao (clt/dashboard:v86) via proxy /clt_simulador/*.
// Fluxo: ficha (CPF do contato) -> autorizar bancos -> ofertas por banco
// (todas empilhadas; melhor do cliente em lima) -> re-simular (AMARELO) ->
// digitar (VERDE) -> link de formalizacao.
// Payloads espelham o app.js v86 (spec extraida do codigo de producao).
// Tokens dos bancos: SEMPRE do Supabase (responsabilidade do digitador).
// ============================================================================

const props = defineProps({
  currentChat: { type: Object, required: true },
});
const emit = defineEmits(['close']);

const onlyDigits = s => (s || '').replace(/\D+/g, '');
const brl = v =>
  (Number(v) || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
const cents = v => brl((Number(v) || 0) / 100);
const ddmmyyyy = iso => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

// ---------------------------------------------------------------- ficha ----
const sender = computed(() => props.currentChat?.meta?.sender || {});
const cpf = ref('');
const nome = ref('');
const nascimento = ref(''); // yyyy-mm-dd
const celular = ref('');
const enrich = ref({});
// De onde veio o celular preenchido: 'contato' (Chatwoot) ou 'consulta'
// (higienização). O operador precisa saber o que está discando.
const origemFone = ref('');

const FONE_SELO = {
  contato: {
    texto: 'do contato',
    classe: 'bg-n-teal-3 text-n-teal-11',
    ajuda: 'Número do contato já existente no Chatwoot',
  },
  consulta: {
    texto: 'da consulta',
    classe: 'bg-n-amber-3 text-n-amber-11',
    ajuda: 'Número veio da higienização (CPF não é contato do Chatwoot)',
  },
  nenhum: {
    texto: 'sem telefone na consulta',
    classe: 'bg-n-ruby-3 text-n-ruby-11',
    ajuda:
      'Nem o Chatwoot nem a higienização têm telefone deste CPF — digite o número.',
  },
};

// Normaliza o que o operador digita, MESMA regra do backend
// (clt_bancos/utils.rb#normalize_phone_br): tira o +55 e insere o 9º dígito.
// Sem isso, "+555189304770" ia inteiro para os bancos, o V8 lia "55" como DDD
// e respondia "phone deve conter 9 dígitos"; a SOMA dizia "Celular inválido!".
const normalizaFoneBr = valor => {
  let d = onlyDigits(valor || '');
  if ((d.length === 12 || d.length === 13) && d.startsWith('55')) d = d.slice(2);
  if (d.length === 10) d = `${d.slice(0, 2)}9${d.slice(2)}`;
  return d;
};

const arrumarCelular = () => {
  const d = normalizaFoneBr(celular.value);
  if (d) celular.value = d;
};

const celularSuspeito = computed(() => {
  const d = onlyDigits(celular.value || '');
  return d.length > 0 && d.length !== 11;
});
const busy = ref('');
const erroGeral = ref('');

// v93: estado do simulador PERSISTE por conversa (fecha e, ao voltar no
// mesmo cliente, reabre exatamente como estava). Cache em memória global.
const SIM_STATE =
  window.__SEGUE_SIM_STATE || (window.__SEGUE_SIM_STATE = new Map());
const stateKey = computed(() => `chat-${props.currentChat?.id}`);

// Bancos ATIVOS no cofre (bank_credentials.enabled) — definem quais colunas
// aparecem. Banco desativado no settings some do simulador. Default = os 4
// suportados enquanto o fetch não volta (evita tela vazia no 1º render).
const bancosAtivos = ref(['uy3', 'facta', 'c6', 'soma']);
const bancoAtivo = b => bancosAtivos.value.includes(b);
const carregarBancosAtivos = async () => {
  try {
    const { data } = await cltApi.get('bancos-ativos');
    if (Array.isArray(data?.bancos)) bancosAtivos.value = data.bancos;
  } catch (e) {
    // Mantém o default dos 4 se a leitura falhar.
  }
};

onMounted(() => {
  carregarBancosAtivos();
  const saved = SIM_STATE.get(stateKey.value);
  if (saved) {
    cpf.value = saved.cpf;
    nome.value = saved.nome;
    nascimento.value = saved.nascimento;
    celular.value = saved.celular;
    enrich.value = saved.enrich;
    auth.value = saved.auth;
    uy3Offers.value = saved.uy3Offers;
    factaState.value = saved.factaState;
    c6Sim.value = saved.c6Sim;
    uy3Resim.value = saved.uy3Resim || {};
    factaResim.value = saved.factaResim || {};
    c6Resim.value = saved.c6Resim || c6Resim.value;
    vinculos.value = saved.vinculos ?? null;
    vinculoSel.value = saved.vinculoSel || '';
    // v117 (Kairo 09/07): restaura a TELA e o FORMULÁRIO de digitação — antes,
    // fechar o modal no meio da digitação e reabrir no mesmo cliente voltava
    // pra "ofertas" com o form vazio (operador perdia tudo que digitou).
    view.value = saved.view || 'ofertas';
    form.value = saved.form || {};
    dig.value = saved.dig ?? null;
    return;
  }
  const ca = sender.value.custom_attributes || {};
  cpf.value = onlyDigits(ca.cpf).slice(0, 11);
  // v94: NÃO puxa o nome do contato — abre só com CPF + telefone; o nome vem
  // do Enriquecer (pedido do Kairo).
  // Mesma normalização de todo o resto (tira DDI, insere o 9º dígito).
  celular.value = normalizaFoneBr(sender.value.phone_number);
});

onBeforeUnmount(() => {
  pararPollSoma();
  pararV8Poll();
  if (v8CdTimer) clearInterval(v8CdTimer);
  SIM_STATE.set(stateKey.value, {
    cpf: cpf.value,
    nome: nome.value,
    nascimento: nascimento.value,
    celular: celular.value,
    enrich: enrich.value,
    auth: auth.value,
    uy3Offers: uy3Offers.value,
    factaState: factaState.value,
    c6Sim: c6Sim.value,
    uy3Resim: uy3Resim.value,
    factaResim: factaResim.value,
    c6Resim: c6Resim.value,
    vinculos: vinculos.value,
    vinculoSel: vinculoSel.value,
    // v117: tela + formulário de digitação
    view: view.value,
    form: form.value,
    dig: dig.value,
  });
});

const cpfOk = computed(() => cpf.value.length === 11);

// v143 (Kairo): LIMPAR — zera SÓ os retornos dos bancos na TELA (nada é
// apagado no banco de dados). Mantém CPF/nome/nascimento/celular e o
// Enriquecer; "Buscar ofertas" traz as consultas salvas de volta.
const limparRetornos = () => {
  pararPollSoma();
  auth.value = null;
  uy3Offers.value = null;
  factaState.value = null;
  c6Sim.value = null;
  somaSim.value = null;
  uy3Resim.value = {};
  factaResim.value = {};
  c6Resim.value = { tipo: 'POR_VALOR_PARCELA', valor: '', prazo: '24', busy: false };
  somaResim.value = {};
  vinculos.value = null;
  vinculoSel.value = '';
  propostas.value = null;
  resultado.value = null;
  erroGeral.value = '';
  // V8: zera tudo do card
  pararV8Poll();
  if (v8CdTimer) clearInterval(v8CdTimer);
  v8Cooldown.value = 0;
  v8Consult.value = null;
  v8Estado.value = null;
  v8Offers.value = null;
  v8Sim.value = null;
  v8Prop.value = null;
  v8SelPrazo.value = '';
  v8DigitarOpen.value = false;
  v8SimErro.value = '';
  v8Form.value = { modo: 'valor_liquido', valor: '', comSeguro: true, pixTipo: 'cpf', pixChave: '', busy: false };
  // limpa os dados do cliente, MANTÉM o CPF
  nome.value = '';
  celular.value = '';
  nascimento.value = '';
};

const enriquecer = async () => {
  if (!cpfOk.value) return;
  busy.value = 'enriquecer';
  erroGeral.value = '';
  try {
    const { data } = await cltApi.get('enriquecer', { cpf: cpf.value });
    enrich.value = data || {};
    if (data.name) nome.value = data.name;
    if (data.birthDate) nascimento.value = data.birthDate.slice(0, 10);
    // Telefone: o do contato do Chatwoot manda (é o número por onde já se
    // falou com o cliente). Consulta AVULSA — CPF que não é contato — não tem
    // esse número: aí vale o que a higienização devolveu, que antes era
    // jogado fora e deixava o campo vazio no meio do atendimento.
    const foneChatwoot = normalizaFoneBr(data.chatwootPhone);
    const foneConsulta = normalizaFoneBr(
      `${data.phoneRegionCode || ''}${data.phone || ''}`
    );
    if (foneChatwoot) {
      celular.value = foneChatwoot;
      origemFone.value = 'contato';
    } else if (foneConsulta) {
      celular.value = foneConsulta;
      origemFone.value = 'consulta';
    } else {
      // Nem contato nem higienização têm número. Sem avisar, o operador fica
      // achando que o sistema falhou — foi exatamente a dúvida do Kairo no
      // CPF 778.336.783-68 (bureau devolveu celular1..5 nulos).
      origemFone.value = 'nenhum';
    }
    useAlert('Dados enriquecidos com sucesso');
  } catch (e) {
    erroGeral.value = `Falha ao enriquecer: ${e.response?.data?.error || e.message}`;
  } finally {
    busy.value = '';
  }
};

// ------------------------------------------------------------ autorizar ----
const auth = ref(null); // { uy3: {...}, facta: {...}, c6: {...} }

const authState = banco => {
  const p = auth.value?.[banco];
  if (!p) return null;
  if (banco === 'soma') {
    // v125: a "autorização" SOMA é a consulta (com auto-aceite no backend)
    const con = Array.isArray(p) ? p[0] : p;
    if (!con || con.detail || con.error)
      return { cls: 'erro', label: String(con?.detail || con?.error || 'ERRO').slice(0, 80) };
    if (con.consultando)
      return { cls: 'aguarda', label: 'CONSULTANDO… (card atualiza sozinho)' };
    const st = String(con.conStatusNome || '');
    if (/n[ãa]o\s*eleg/i.test(st)) return { cls: 'erro', label: 'NÃO ELEGÍVEL' };
    if (/eleg/i.test(st)) return { cls: 'ok', label: 'ELEGÍVEL' };
    if (con.conLinkAssinatura && !con.conAssinadoEm)
      return { cls: 'aguarda', label: 'AGUARDANDO ASSINATURA DO TERMO' };
    return { cls: 'aguarda', label: (st || 'CONSULTADO').slice(0, 60) };
  }
  if (banco === 'c6') {
    // v123: autorização com +30 dias -> operador decide gerar novo link
    if (p.autorizacao_30d)
      return { cls: 'expirado', label: 'AUTORIZAÇÃO +30 DIAS — gerar novo?' };
    // v122: cliente AUTORIZADO -> nunca gera outro link (segue pra consulta),
    // mesmo com o link expirado (v123)
    if (p.ja_autorizado || p.status === 'AUTORIZADO')
      return { cls: 'ok', label: 'JÁ AUTORIZADO — siga para a consulta' };
    // v122: link expirado e não autorizado -> operador decide gerar outro
    if (p.expirado || p.pode_gerar_novo)
      return { cls: 'expirado', label: 'LINK EXPIRADO — gerar novo?' };
    const link = p.url || p.link || p.liveness_url;
    if (link)
      return {
        cls: 'roxo',
        label: p.status === 'LINK REAPROVEITADO' ? 'LINK REAPROVEITADO' : 'LINK GERADO',
        link,
      };
    return { cls: 'erro', label: p.error || p.detail || p.mensagem || 'ERRO' };
  }
  if (banco === 'uy3') {
    if (p.code === 'EXISTING_AUTH') return { cls: 'ok', label: 'JÁ AUTORIZADO' };
    if (p.error || p.detail) return { cls: 'erro', label: String(p.error || p.detail).slice(0, 80) };
    return { cls: 'aguarda', label: 'AGUARDANDO CLIENTE (termo enviado)' };
  }
  if (banco === 'v8') {
    if (p.error || p.detail) return { cls: 'erro', label: String(p.error || p.detail).slice(0, 80) };
    const st = String(v8Estado.value?.status || p.status || '');
    if (st === 'SUCCESS') return { cls: 'ok', label: 'MARGEM OK' };
    if (/ERROR|FAIL|REPRO/i.test(st)) return { cls: 'erro', label: st };
    return { cls: 'aguarda', label: 'CONSULTANDO MARGEM…' };
  }
  // facta
  const item = Array.isArray(p) ? p[0] : p;
  if (item?.erro === false) {
    const msg = String(item.mensagem || item.message || '').toLowerCase();
    if (/não necessita|ja autoriz|já autoriz|válido|valido/.test(msg))
      return { cls: 'ok', label: 'JÁ AUTORIZADO' };
    return { cls: 'aguarda', label: 'LINK ENVIADO (WhatsApp)' };
  }
  return {
    cls: item?.erro ? 'erro' : 'aguarda',
    label: String(item?.mensagem || item?.message || 'LINK ENVIADO').slice(0, 80),
  };
};

// v139 (Kairo): ao AUTORIZAR, se JÁ EXISTE consulta DO DIA pro CPF, abre a
// janela "Existe consulta para esse CPF" com NOVA CONSULTA | CONTINUAR.
// CONTINUAR reaproveita as consultas do dia; NOVA CONSULTA refaz e salva
// (nunca apaga nada). Virou o dia (00:00) ou gerou proposta -> consulta nova.
const autorizarDialog = ref(false);

const autorizar = async () => {
  if (!cpfOk.value || !nome.value || !celular.value || !nascimento.value) {
    erroGeral.value =
      'Preencha CPF, nome, celular e nascimento (use Enriquecer).';
    return;
  }
  // já existe consulta do dia? (estado é leve — não consome o limite do SOMA)
  try {
    const { data: est } = await cltApi.get('soma/estado', {
      cpf: cpf.value,
      bancarizadora: somaBanca.value,
    });
    if (!est.pendente) {
      autorizarDialog.value = true;
      return;
    }
  } catch (e) {
    // segue direto
  }
  executarAutorizar(false);
};

const executarAutorizar = async novaConsulta => {
  autorizarDialog.value = false;
  busy.value = 'autorizar';
  erroGeral.value = '';
  try {
    const { data } = await cltApi.post('multi/autorizar', {
      cpf: cpf.value,
      nome: nome.value,
      celular: onlyDigits(celular.value),
      data_nascimento: nascimento.value,
      bancos: bancosAtivos.value,
      soma_bancarizadora: somaBanca.value,
      soma_force: novaConsulta ? '1' : null,
    });
    auth.value = data;
    // v133: SOMA autoriza/consulta em background — card atualiza sozinho
    if (data?.soma?.consultando) {
      somaSim.value = { consultando: true };
      iniciarPollSoma();
    } else if (data?.soma && (data.soma.conId || Array.isArray(data.soma))) {
      // consulta do dia reaproveitada — já preenche o card
      aplicarSomaEstado(parseSomaConsulta(data.soma));
    }
  } catch (e) {
    erroGeral.value = `Falha ao autorizar: ${e.response?.data?.error || e.message}`;
  } finally {
    busy.value = '';
  }
};

const copiar = async (txt, oQue = 'Link') => {
  await copyTextToClipboard(txt);
  useAlert(`${oQue} copiado`);
};

// v122: link C6 EXPIROU e o cliente não autorizou — o operador confirmou que
// quer gerar um novo (force=true fura só o reaproveitamento; AUTORIZADO o
// backend continua NUNCA gerando).
const gerarNovoLinkC6 = async () => {
  const digits = onlyDigits(celular.value);
  busy.value = 'c6novolink';
  try {
    const { data } = await cltApi.post('c6/gerar-link', {
      cpf: cpf.value,
      nome: nome.value,
      data_nascimento: nascimento.value,
      ddd: digits.slice(0, 2),
      telefone: digits.slice(2),
      force: true,
    });
    auth.value = { ...(auth.value || {}), c6: data };
    useAlert(data.link || data.url ? 'Novo link C6 gerado' : 'C6: veja o status no card');
  } catch (e) {
    useAlert(`C6: ${e.response?.data?.detail || e.message}`);
  } finally {
    busy.value = '';
  }
};

// v123: mensagem transitória DENTRO da janela do C6 (10s e some).
const c6Msg = ref('');
let c6MsgTimer = null;
const mostrarMsgC6 = txt => {
  c6Msg.value = txt;
  clearTimeout(c6MsgTimer);
  c6MsgTimer = setTimeout(() => {
    c6Msg.value = '';
  }, 10000);
};

// v123: botão CONSULTAR AUTORIZAÇÃO do card C6 — consulta o status ao vivo e
// mostra o retorno SÓ na janela do C6. Se autorizou, carrega a oferta.
const consultarAutorizacaoC6 = async () => {
  busy.value = 'c6consulta';
  try {
    const { data: est } = await cltApi.get('c6/consulta-autorizacao', {
      cpf: cpf.value,
    });
    if (est.autorizado) {
      mostrarMsgC6('AUTORIZADO ✅ — buscando a oferta…');
      await buscarC6();
    } else if (!est.existe) {
      mostrarMsgC6('Sem registro de autorização — use "Autorizar bancos".');
    } else if (est.link_valido) {
      mostrarMsgC6(`${est.status || 'UNAUTHORIZED'} — cliente ainda não concluiu a biometria.`);
    } else {
      mostrarMsgC6(`${est.status || 'UNAUTHORIZED'} — link EXPIRADO.`);
      c6Sim.value = { expirado: true };
    }
  } catch (e) {
    mostrarMsgC6(`Erro: ${e.response?.data?.detail || e.message}`);
  } finally {
    busy.value = '';
  }
};

// ============================================================== SOMA (v125) --
// Banco SOMA (somabp2), modelo do card UY3. Consulta (com auto-aceite no
// backend) -> simulações; re-sim por valor-parcela/valor-liberado + prazo;
// Não Elegível mostra conMensagem + motivosMapeados; DIGITAR = cliente
// (endereço com código IBGE via facta/cidade) + proposta.
const somaSim = ref(null); // null | {aguardando} | {naoElegivel,mensagem,motivos} | {con, sims:[]}
const somaBanca = ref('CELCOIN'); // bancarizadora do fluxo inteiro (CELCOIN|UY3)
// v142: re-sim POR CARD — cada simulação tem seu próprio tipo/valor/prazo
const somaResim = ref({}); // idx -> {tipo, valor, prazo, busy}
const seedSomaResim = () => {
  const sims = somaSim.value?.sims || [];
  const novo = {};
  sims.forEach((sim, i) => {
    novo[i] = somaResim.value[i] || {
      tipo: 'valor-liberado',
      valor: '',
      prazo: String(sim.simPrazo || 36),
      busy: false,
    };
  });
  novo.vazio = somaResim.value.vazio || {
    tipo: 'valor-liberado', valor: '', prazo: '36', busy: false,
  };
  somaResim.value = novo;
};
// aplica o estado do SOMA no card e semeia os controles de re-sim
const aplicarSomaEstado = parsed => {
  somaSim.value = parsed;
  seedSomaResim();
};
const SOMA_PRAZOS = [6, 8, 12, 18, 24, 36, 48, 60]; // v128: prazos clicáveis
const somaMsg = ref('');
let somaMsgTimer = null;
const mostrarMsgSoma = txt => {
  somaMsg.value = txt;
  clearTimeout(somaMsgTimer);
  somaMsgTimer = setTimeout(() => {
    somaMsg.value = '';
  }, 10000);
};

// v130 (Kairo): bancarizadora agora troca DENTRO do card da simulação —
// muda e re-consulta na hora (as simulações recarregam na bancarizadora nova).
const trocarBancaSoma = async b => {
  if (somaBanca.value === b) return;
  somaBanca.value = b;
  mostrarMsgSoma(`Consultando na ${b}…`);
  await buscarSOMA();
  mostrarMsgSoma(`Bancarizadora: ${b}`);
};

// Normaliza o retorno da consulta SOMA (array ou objeto) pro estado do card.
const parseSomaConsulta = data => {
  if (data?.consultando) return { consultando: true };
  const con = Array.isArray(data) ? data[0] : data;
  if (!con || con.detail || con.error)
    return { erro: String(con?.detail || con?.error || 'sem retorno').slice(0, 160) };
  const st = String(con.conStatusNome || '');
  if (/aguardando/i.test(st)) return { aguardando: true, con };
  if (/n[ãa]o\s*eleg/i.test(st)) {
    const motivos = (con.motivosMapeados || [])
      .map(m => (typeof m === 'string' ? m : m?.motivo || m?.descricao || JSON.stringify(m)))
      .filter(Boolean);
    return { naoElegivel: true, mensagem: con.conMensagem || '', motivos, con };
  }
  const sims = (con.simulacoes || []).filter(s => s && s.simId);
  return { con, sims };
};

// v133 (modelo FACTA): o SOMA NÃO bloqueia mais os outros bancos. Consulta
// pesada roda em background no servidor; aqui a gente lê o ESTADO salvo e,
// se estiver consultando, fica pingando a cada 5s — o card atualiza SOZINHO.
let somaPollTimer = null;
const pararPollSoma = () => {
  clearInterval(somaPollTimer);
  somaPollTimer = null;
};
const iniciarPollSoma = () => {
  pararPollSoma();
  let tentativas = 0;
  somaPollTimer = setInterval(async () => {
    tentativas += 1;
    if (tentativas > 36) {
      pararPollSoma(); // ~3min — desiste do poll (Buscar ofertas re-tenta)
      if (somaSim.value?.consultando)
        somaSim.value = { erro: 'SOMA demorou — clique em Buscar ofertas de novo.' };
      return;
    }
    try {
      const { data } = await cltApi.get('soma/estado', {
        cpf: cpf.value,
        bancarizadora: somaBanca.value,
      });
      if (data.erro) {
        pararPollSoma();
        somaSim.value = { erro: String(data.erro).slice(0, 160) };
        return;
      }
      if (!data.pendente) {
        pararPollSoma();
        aplicarSomaEstado(parseSomaConsulta(data));
      }
    } catch (e) {
      // segue pingando
    }
  }, 5000);
};

// v135: botão Atualizar (igual FACTA) — checa o estado NA HORA; se o retorno
// já chegou, preenche o card; senão continua consultando/polling.
const atualizarSOMA = async () => {
  try {
    const { data } = await cltApi.get('soma/estado', {
      cpf: cpf.value,
      bancarizadora: somaBanca.value,
    });
    if (data.erro) {
      pararPollSoma();
      somaSim.value = { erro: String(data.erro).slice(0, 160) };
      return;
    }
    if (!data.pendente) {
      pararPollSoma();
      aplicarSomaEstado(parseSomaConsulta(data));
    } else {
      mostrarMsgSoma(
        data.consultando
          ? 'Ainda consultando — o card atualiza sozinho.'
          : 'Sem retorno ainda — clique em Buscar ofertas para tentar de novo.'
      );
    }
  } catch (e) {
    // mantém consultando
  }
};

const buscarSOMA = async () => {
  try {
    const { data: est } = await cltApi.get('soma/estado', {
      cpf: cpf.value,
      bancarizadora: somaBanca.value,
    });
    if (!est.pendente) {
      aplicarSomaEstado(parseSomaConsulta(est));
      return;
    }
    if (est.erro && !est.consultando) {
      somaSim.value = { erro: String(est.erro).slice(0, 160) };
      return;
    }
    // sem consulta válida: dispara (backend responde na hora e trabalha em bg)
    if (!est.consultando) {
      await cltApi.post('soma/consultar', {
        cpf: cpf.value,
        nome: nome.value,
        celular: onlyDigits(celular.value),
        dataNascimento: nascimento.value,
        bancarizadora: somaBanca.value,
      });
    }
    somaSim.value = { consultando: true };
    iniciarPollSoma();
  } catch (e) {
    somaSim.value = {
      erro: (e.response?.data?.detail || e.message || '').slice(0, 160),
    };
  }
};

// Re-sim SOMA: por valor da parcela ou valor liberado, com prazo. "Sem fundo
// pro prazo" -> só mensagem na janela do SOMA (mantém as simulações atuais).
const resimSOMA = async si => {
  const st = somaResim.value[si];
  if (!st) return;
  const conId = somaSim.value?.con?.conId;
  const valor = numBR(st.valor);
  const prazo = parseInt(st.prazo, 10);
  if (!conId || !valor || !prazo) {
    mostrarMsgSoma('Informe o VALOR e o PRAZO para simular.');
    return;
  }
  st.busy = true;
  try {
    const body = {
      conId,
      tipo: st.tipo,
      parcelas: prazo,
      bancarizadora: somaBanca.value,
      cpf: cpf.value,
    };
    if (st.tipo === 'valor-parcela') body.valorParcela = valor;
    else body.valor = valor;
    const { data } = await cltApi.post('soma/simular', body);
    const sim = Array.isArray(data) ? data[0] : data?.simulacao || data;
    if (sim && sim.simId) {
      // v142: atualiza SÓ ESTE card (substitui valores + ID); demais intactos.
      // Cada simulação segue gravada como linha nova na bank_simulation_soma.
      const sims = [...(somaSim.value?.sims || [])];
      if (si === 'vazio' || !sims.length) sims.push(sim);
      else sims[si] = sim;
      somaSim.value = { ...somaSim.value, erro: null, sims };
      st.prazo = String(sim.simPrazo || st.prazo);
      mostrarMsgSoma('SOMA re-simulado');
      seedSomaResim();
    } else {
      mostrarMsgSoma(
        String(
          data?.mensagem || data?.detail || data?.message || 'simulação indisponível para esse prazo'
        ).slice(0, 160)
      );
    }
  } catch (e) {
    mostrarMsgSoma(somaErroLegivel(e)); // mensagem real do banco
  } finally {
    st.busy = false;
  }
};

const SOMA_ESTADO_CIVIL = {
  solteiro: 'SOLTEIRO',
  casado: 'CASADO',
  divorciado: 'DIVORCIADO',
  viuvo: 'VIUVO',
  separado: 'SEPARADO',
  uniao: 'UNIAO_ESTAVEL',
};
const SOMA_PIX_TIPOS = ['CPF', 'TELEFONE', 'EMAIL', 'CHAVE_ALEATORIA'];

const abrirDigitarSOMA = sim => {
  dig.value = {
    banco: 'SOMA',
    ctx: { con: somaSim.value?.con, sim },
    resumo: {
      valor: brl(numBR(sim.simValorLiquido)),
      prazo: `${sim.simPrazo}x`,
      parcela: brl(numBR(sim.simValorParcela)),
      extra: `IOF ${brl(numBR(sim.simValorIof))} · taxa ${sim.simTaxaMensal}%`,
    },
  };
  form.value = {
    ...baseForm(),
    sexo: 'M',
    estadoCivil: 'solteiro',
    nomeMae: enrich.value.motherName || '',
    occupation: 'Trabalhador',
    pixTipo: 'CPF',
    pixChave: cpf.value,
    // v128: o SOMA exige os dados bancários COMPLETOS mesmo com PIX por chave
    // (validação real da API diverge da doc) — campos como no C6.
    tipoConta: 'CONTA_CORRENTE',
    numeroBanco: '',
    numeroAgencia: '',
    digitoAgencia: '',
    numeroConta: '',
    digitoConta: '',
  };
  view.value = 'digitar';
};

// v128: transforma o erro de validação do SOMA ({message, detalhes[]}) numa
// mensagem legível campo a campo pro operador corrigir na tela.
const somaErroLegivel = e => {
  const d = e.response?.data;
  if (d?.detalhes?.length) {
    const campos = d.detalhes
      .map(x => `${String(x.campo || '').split('/').pop()}: ${x.mensagem}`)
      .join(' · ');
    return `${d.message || 'Erro de validação'} — ${campos}`.slice(0, 400);
  }
  return String(d?.mensagem || d?.message || d?.detail || e.message).slice(0, 300);
};

// v127: código IBGE da cidade — 1º pelo CEP no ViaCEP (campo `ibge`, fonte
// oficial e imune a distrito no lugar do município, ex.: "RIVE/ES").
// v145: fallback pela API OFICIAL do IBGE — a chave do mapa facta/cidade é
// o código INTERNO da FACTA, NÃO o IBGE (caso Gentio do Ouro/BA: FACTA
// 28340 vs IBGE 2911303 → FK cliente_enderecos_end_cidade_id_fkey no SOMA).
const somaCidadeIbge = async f => {
  const cepd = onlyDigits(f.cep);
  if (cepd.length === 8) {
    try {
      const r = await fetch(`https://viacep.com.br/ws/${cepd}/json/`);
      const d = await r.json();
      if (!d.erro && d.ibge) return String(d.ibge);
    } catch (e) {
      // segue pro fallback
    }
  }
  try {
    const norm = s =>
      String(s || '')
        .toUpperCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    const uf = (f.uf || '').trim().toUpperCase();
    const r = await fetch(
      `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios`
    );
    const lista = await r.json();
    const alvo = norm(f.cidade);
    const hit = Array.isArray(lista) && lista.find(m => norm(m.nome) === alvo);
    if (hit) return String(hit.id);
  } catch (e) {
    // sem resolução — o chamador orienta conferir CEP/cidade
  }
  return null;
};

const digitarSOMA = async () => {
  const f = form.value;
  const con = dig.value.ctx.con || {};
  const sim = dig.value.ctx.sim || {};

  // v141: valida ANTES e mostra O QUE FALTA (pedido Kairo)
  const faltando = [];
  if (!f.nome) faltando.push('Nome');
  if (!f.nascimento) faltando.push('Nascimento');
  if (!f.nomeMae) faltando.push('Nome da mãe');
  if (!f.email) faltando.push('E-mail');
  if (!onlyDigits(f.telefone)) faltando.push('Telefone');
  if (!onlyDigits(f.cep)) faltando.push('CEP');
  if (!f.rua) faltando.push('Rua');
  if (!f.numero) faltando.push('Número');
  if (!f.bairro) faltando.push('Bairro');
  if (!f.cidade) faltando.push('Cidade');
  if (!f.uf) faltando.push('UF');
  const temPix = !!(f.pixTipo && f.pixChave);
  const temConta = !!(
    onlyDigits(f.numeroBanco) &&
    onlyDigits(f.numeroAgencia) &&
    onlyDigits(f.numeroConta)
  );
  if (!temPix && !temConta)
    faltando.push('Pagamento (chave PIX OU dados bancários completos)');
  if (faltando.length) throw new Error(`SOMA — faltando: ${faltando.join(', ')}`);

  const cidadeIbge = await somaCidadeIbge(f);
  if (!cidadeIbge)
    throw new Error(
      `SOMA: não achei o código IBGE de "${f.cidade}/${f.uf}". Confira o CEP (Buscar CEP) ou corrija a cidade — use o MUNICÍPIO, não o distrito.`
    );

  // v142: DIGITAÇÃO EM ETAPAS pela rota da BANCARIZADORA (fluxo validado
  // pelo Kairo 14/07) — cliente -> endereço -> conta -> vincular (CELCOIN) ->
  // proposta, uma chamada por vez, com erro claro de qual etapa falhou.
  // ETAPA 1 — cadastrar cliente (retorna cliId)
  let cli;
  try {
    ({ data: cli } = await cltApi.post('soma/cliente', {
      bancarizadora: somaBanca.value,
      cliente: {
        cliCpf: cpf.value,
        cliNome: f.nome,
        cliNascimento: f.nascimento,
        cliNomeMae: f.nomeMae,
        cliProfissao: f.occupation || 'Trabalhador',
        cliNacionalidade: 'BRASILEIRA',
        cliSexo: f.sexo === 'F' ? 'FEMININO' : 'MASCULINO',
        cliEstadoCivil: SOMA_ESTADO_CIVIL[f.estadoCivil] || 'SOLTEIRO',
        cliPoliticamenteExposta: false,
        cliEmail: f.email,
        cliCelular: onlyDigits(f.telefone),
      },
    }));
  } catch (e) {
    throw new Error(`SOMA etapa 1 (cliente): ${somaErroLegivel(e)}`);
  }
  const clienteId = cli.clienteId || cli.cliId || cli.id;
  if (!clienteId) throw new Error('SOMA etapa 1: cliente sem clienteId no retorno');

  // ETAPA 2 — endereço
  try {
    await cltApi.post('soma/endereco', {
      clienteId,
      endereco: {
        endCep: onlyDigits(f.cep),
        endRua: f.rua,
        endNumero: f.numero,
        endBairro: f.bairro,
        endComplemento: f.complemento || null,
        endCidadeId: Number(cidadeIbge),
        endEstadoId: Number(String(cidadeIbge).slice(0, 2)),
      },
    });
  } catch (e) {
    throw new Error(`SOMA etapa 2 (endereço): ${somaErroLegivel(e)}`);
  }

  // ETAPA 3 — conta (PIX por chave OU dados bancários; nulls explícitos no backend)
  let contaResp;
  try {
    const chave =
      f.pixTipo === 'TELEFONE' ? onlyDigits(f.pixChave) : String(f.pixChave || '').trim();
    ({ data: contaResp } = await cltApi.post('soma/conta', {
      clienteId,
      contaBancaria: temConta
        ? {
            conTipoConta: f.tipoConta || 'CONTA_CORRENTE',
            conBancoId: onlyDigits(f.numeroBanco),
            conAgencia: onlyDigits(f.numeroAgencia),
            conDigitoAgencia: f.digitoAgencia || null,
            conConta: onlyDigits(f.numeroConta),
            conDigitoConta: f.digitoConta || null,
            conTipoPix: temPix ? f.pixTipo : null,
            conChavePix: temPix ? chave : null,
          }
        : {
            conTipoPix: f.pixTipo,
            conChavePix: chave,
          },
    }));
  } catch (e) {
    throw new Error(`SOMA etapa 3 (conta): ${somaErroLegivel(e)}`);
  }
  const contaId =
    contaResp.contaBancariaId || contaResp.conId || contaResp.contaId || contaResp.id;
  if (!contaId) throw new Error('SOMA etapa 3: conta sem contaBancariaId no retorno');

  // ETAPA 4 — vincular conta ao cliente (etapa exclusiva da CELCOIN)
  if (somaBanca.value === 'CELCOIN') {
    try {
      // proxy só roteia GET/POST; o backend converte em PUT pro SOMA
      await cltApi.post('soma/vincular-conta', {
        clienteId,
        contaBancariaId: contaId,
      });
    } catch (e) {
      throw new Error(`SOMA etapa 4 (vincular conta): ${somaErroLegivel(e)}`);
    }
  }

  // ETAPA 5 — cadastrar proposta
  let prop;
  try {
    ({ data: prop } = await cltApi.post('soma/proposta', {
      bancarizadora: somaBanca.value,
      cpf: cpf.value,
      clienteId,
      consultaId: con.conId,
      simulacaoId: sim.simId,
      contaBancariaId: contaId,
    }));
  } catch (e) {
    throw new Error(`SOMA etapa 5 (proposta): ${somaErroLegivel(e)}`);
  }
  const numero = prop.proId || prop.propostaId || prop.id || prop.numero_proposta;
  if (!numero && (prop.detail || prop.error))
    throw new Error(String(prop.detail || prop.error).slice(0, 200));
  resultado.value = {
    banco: 'SOMA',
    ok: true,
    numero: numero || '(cadastrada)',
    link: prop.proLinkAssinatura || null,
    msg: 'Proposta SOMA cadastrada com sucesso.',
  };
  view.value = 'resultado';
};

// v122: parser de dinheiro BR ("13.471,62" -> 13471.62). Remove os pontos
// ANTES da vírgula — parseFloat("3.500,90") daria 3.5.
const numBR = v => {
  if (typeof v === 'number') return v;
  const s = String(v ?? '').replace(/[^\d.,-]/g, '').trim(); // v136: aceita "R$ 500,00"
  if (!s) return 0;
  return Number(s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s) || 0;
};

// v136: máscara de moeda BR ao digitar (estilo banco: dígitos viram centavos)
// "50000" -> "R$ 500,00". Usada no campo de valor do C6.
const mascaraMoedaBR = raw => {
  const d = onlyDigits(raw);
  if (!d) return '';
  return (Number(d) / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};
const C6_PRAZOS = [6, 12, 18, 24, 36, 48, 60]; // v136: prazos clicáveis do C6

// v93: copia a oferta formatada pro cliente (texto do Kairo), com o PRIMEIRO
// nome vindo da consulta/enriquecer.
const primeiroNome = computed(() => {
  const n = (nome.value || 'cliente').trim().split(/\s+/)[0] || 'cliente';
  return n.charAt(0).toUpperCase() + n.slice(1).toLowerCase();
});

const copiarOferta = async ({ valor, prazo, parcela }) => {
  const txt =
    `Boa notícia, ${primeiroNome.value}! Consegui uma oferta pra você 💚\n\n` +
    `💰 Valor liberado: ${valor}\n` +
    `📋 Parcelas: ${prazo}x de ${parcela}\n\n` +
    `Posso seguir com a contratação?`;
  await copyTextToClipboard(txt);
  useAlert('Oferta copiada');
};

// -------------------------------------------------------------- ofertas ----
const uy3Offers = ref(null); // [{o, w}] ordenado desc
const uy3Motivo = ref(''); // v140: retorno do UY3 quando não elegível
const factaState = ref(null); // {status, ofertas[], vinculos[], motivo}
const c6Sim = ref(null); // {..} | {aguardando:true} | {erro}
const buscando = ref(false);

// ---------------------------------------------------------- vínculos (v107) --
// O operador escolhe o VÍNCULO EMPREGATÍCIO antes das ofertas: cada card fica
// AZUL (ligado/elegível) ou VERMELHO (desligado/bloqueado — não selecionável).
// Fontes: FACTA (ofertas-consolidado já manda vinculos[]), UY3 consultar-margem
// (FreeMarginQuery) e, como garantia, o warranty embutido em cada oferta UY3.
// A amarração vínculo→oferta→digitação é pela MATRÍCULA, que já viaja em toda
// digitação (FACTA: of.matricula; UY3: warranty.dataprev_EmployeeCode) — por
// isso filtrar as ofertas pelo vínculo já amarra a digitação automaticamente.
const vinculos = ref(null); // null = não buscado; [] = nenhum encontrado
const vinculoSel = ref(''); // matrícula selecionada

const normStr = v => String(v ?? '').trim();

// desligado = bloqueio explícito ou elegivel explicitamente negativo
const vincLigado = v => {
  const bloqueio = normStr(
    v.tipoBloqueio_descricao || (v.tipoBloqueio && v.tipoBloqueio.descricao)
  );
  if (bloqueio && !/sem bloqueio/i.test(bloqueio)) return false;
  const eleg = v.elegivel;
  if (eleg === false) return false;
  const elegStr = normStr(eleg).toLowerCase();
  if (elegStr === 'nao' || elegStr === 'não' || elegStr === 'false') return false;
  return true;
};

const buscarMargemUY3 = async () => {
  try {
    const { data } = await cltApi.post('uy3/consultar-margem', { cpf: cpf.value });
    return data;
  } catch (e) {
    return null; // best-effort: FACTA e warranties das ofertas cobrem
  }
};

const montarVinculos = margemUy3 => {
  const mapa = new Map();
  const add = (v, fonte) => {
    const mat = normStr(v.matricula || v.dataprev_EmployeeCode);
    if (!mat) return;
    const cur = mapa.get(mat) || {
      matricula: mat,
      empregador: '',
      cnpj: '',
      admissao: '',
      salario: 0,
      margem: 0,
      ligado: true,
      motivo: '',
      fontes: [],
    };
    cur.empregador =
      cur.empregador ||
      normStr(
        v.razaoSocialEmpregador ||
          v.nomeEmpregador ||
          v.nomeRazaoSocialEmpregador ||
          v.dataprev_EmployerName
      );
    cur.cnpj = cur.cnpj || normStr(v.numeroInscricaoEmpregador || v.dataprev_EmployerRegistrationNumber);
    cur.admissao = cur.admissao || normStr(v.dataAdmissao || v.dataprev_EmployeeAdmissionDate);
    cur.salario = cur.salario || Number(v.valorTotalVencimentos ?? v.valorSalario) || 0;
    cur.margem = cur.margem || Number(v.valorMargemDisponivel ?? v.margemDisponivel) || 0;
    if (!vincLigado(v)) {
      cur.ligado = false;
      cur.motivo =
        cur.motivo ||
        normStr(
          v.tipoBloqueio_descricao ||
            (v.tipoBloqueio && v.tipoBloqueio.descricao) ||
            v.motivoInelegibilidade_descricao ||
            (v.motivoInelegibilidade && v.motivoInelegibilidade.descricao) ||
            v.motivoInelegibilidade
        );
    }
    if (!cur.fontes.includes(fonte)) cur.fontes.push(fonte);
    mapa.set(mat, cur);
  };
  // UY3 consultar-margem (cru: array de datasets com result[])
  const listaUy3 = Array.isArray(margemUy3)
    ? margemUy3.flatMap(d => (Array.isArray(d?.result) ? d.result : []))
    : [];
  listaUy3.forEach(v => add(v, 'UY3'));
  // FACTA (backend já filtra pelos elegíveis)
  (factaState.value?.vinculos || []).forEach(v => add(v, 'FACTA'));
  // garantia: empregador do warranty de cada oferta UY3 (toda oferta tem vínculo)
  (uy3Offers.value || []).forEach(c =>
    add({ ...c.w, matricula: c.w?.dataprev_EmployeeCode }, 'UY3')
  );
  // ligados primeiro
  return [...mapa.values()].sort((a, b) => (b.ligado ? 1 : 0) - (a.ligado ? 1 : 0));
};

const selecionarVinculo = v => {
  if (!v.ligado) return;
  vinculoSel.value = v.matricula;
};

// ofertas só aparecem sem seleção quando NÃO existe vínculo algum (fallback:
// nunca travar a tela se as fontes não devolverem vínculo)
const mostrarOfertas = computed(
  () => !(vinculos.value || []).length || !!vinculoSel.value
);
// oferta sem matrícula não tem como amarrar — continua visível
const uy3CardVisivel = card => {
  const mat = normStr(card.w?.dataprev_EmployeeCode);
  return !vinculoSel.value || !mat || mat === vinculoSel.value;
};
const factaCardVisivel = of => {
  const mat = normStr(of.matricula);
  return !vinculoSel.value || !mat || mat === vinculoSel.value;
};

const uy3ParcelaReal = o => {
  const items = o.paymentScheduleItems;
  if (Array.isArray(items)) {
    const p = items.find(i => Number(i.payment) > 0);
    if (p) return Number(p.payment);
  }
  return null;
};

// v100: quebra o productName da UY3 pro card novo — "LEILÃO - CLT - 3C -
// Seguro Sabemi - V2" vira Produto "CLT - 3C" (LEILÃO já é badge e o lugar
// do seguro agora mostra o ID da simulação). Sem seguro/leilão, mostra tudo.
const uy3Produto = pn => {
  let s = String(pn || '').replace(/leil[aã]o\s*-?\s*/i, '');
  const ix = s.search(/seguro/i);
  if (ix >= 0) s = s.slice(0, ix);
  return s.replace(/^[\s·-]+|[\s·-]+$/g, '').trim() || '—';
};

const buscarUY3 = async () => {
  try {
    const { data } = await cltApi.post('uy3/ofertas', {
      cpf: cpf.value,
      rangeNumberOfPayments: [12, 18, 24, 36, 48, 60, 72, 84],
    });
    const list = Array.isArray(data) ? data : data?.offers || data?.result || [];
    uy3Offers.value = list
      .filter(x => x && x.amortization)
      .map(x => ({ o: x.amortization, w: x.warranty || {} }))
      .sort((a, b) => (b.o.liquidValue || 0) - (a.o.liquidValue || 0));
    uy3Resim.value = {};
    uy3Offers.value.forEach((_, i) => {
      uy3Resim.value[i] = { modo: 'Liquid', valor: '', busy: false, erro: '' };
    });
  } catch (e) {
    uy3Offers.value = [];
    // v140: guarda o retorno do UY3 pra mostrar NÃO ELEGÍVEL no card
    uy3Motivo.value = String(
      e.response?.data?.detail || e.response?.data?.message || e.message || ''
    ).slice(0, 300);
    return e.response?.data?.detail || e.message;
  }
  uy3Motivo.value = '';
  return null;
};

const buscarFACTA = async () => {
  try {
    cltApi
      .post('facta/log-busca-ofertas', {
        cpf: cpf.value,
        data_nascimento: ddmmyyyy(nascimento.value),
      })
      .catch(() => {});
    const { data } = await cltApi.post('facta/ofertas-consolidado', {
      cpf: cpf.value,
      data_nascimento: ddmmyyyy(nascimento.value),
      telefone: onlyDigits(celular.value),
    });
    const ofertas = (data.ofertas || [])
      .slice()
      .sort((a, b) => (b.valor_liquido || 0) - (a.valor_liquido || 0));
    factaState.value = {
      status: data.status,
      ofertas,
      vinculos: data.vinculos || [],
      motivo: data.motivo_negado || '',
    };
    factaResim.value = {};
    ofertas.forEach((of, i) => {
      factaResim.value[i] = { parcela: of.parcela, busy: false, erro: '' };
    });
  } catch (e) {
    factaState.value = {
      status: 'erro',
      ofertas: [],
      vinculos: [],
      motivo: e.response?.data?.detail || e.message,
    };
  }
};

const C6_WAIT_RE = /WAITING|PENDING|NOT_AUTH|invalid request body|not-auth|liveness|consent/i;

// v122: fluxo Kairo — 1º consulta a autorização (bank_consultations_c6 +
// status ao vivo). AUTORIZADO -> busca a OFERTA MÁXIMA (c6/oferta). Pendente
// com link válido -> aguardando (mostra o link). Expirado -> pergunta.
const buscarC6 = async () => {
  try {
    const { data: est } = await cltApi.get('c6/consulta-autorizacao', {
      cpf: cpf.value,
    });
    if (!est.existe) {
      c6Sim.value = { semlink: true };
      return;
    }
    if (!est.autorizado) {
      c6Sim.value = est.link_valido
        ? { aguardando: true, link: est.link, data_expiracao: est.data_expiracao }
        : { expirado: true };
      return;
    }
    const { data } = await cltApi.post('c6/oferta', { cpf: cpf.value });
    const t = data?.trabalhador;
    if (t && numBR(t.valor_cliente) > 0) {
      c6Sim.value = {
        oferta: {
          valor_cliente: numBR(t.valor_cliente),
          quantidade_parcelas: parseInt(t.quantidade_parcelas, 10) || 0,
          valor_parcela: numBR(t.valor_parcela),
          valor_taxa: numBR(t.valor_taxa),
          valor_seguro: numBR(t.seguro?.valor_seguro),
        },
        condicoes: null,
      };
    } else if (C6_WAIT_RE.test(JSON.stringify(data)))
      c6Sim.value = { aguardando: true };
    else c6Sim.value = { erro: data.detail || data.error || 'sem oferta' };
  } catch (e) {
    const raw = JSON.stringify(e.response?.data || e.message);
    c6Sim.value = C6_WAIT_RE.test(raw)
      ? { aguardando: true }
      : { erro: (e.response?.data?.detail || e.message || '').slice(0, 120) };
  }
};

// v122: extrai as condições VÁLIDAS da simulação v2 (lista condicoes[].condicao
// ou objeto único no shape antigo). Zerada/"taxa fora dos limites" fica fora.
const c6ExtraiCondicoes = data => {
  // v135: campo real da simulação v2 do C6 = condicoes_credito
  const lista =
    data?.condicoes_credito || data?.condicoes || data?.propostas || data?.simulacoes;
  let conds = [];
  if (Array.isArray(lista)) conds = lista.map(x => x?.condicao || x).filter(Boolean);
  else if (data?.id_simulacao) conds = [data];
  return conds.filter(
    c =>
      c.id_simulacao &&
      (parseInt(c.quantidade_parcelas, 10) || 0) > 0 &&
      numBR(c.valor_parcela) > 0
  );
};

const buscarOfertas = async () => {
  if (!cpfOk.value || !nascimento.value) {
    erroGeral.value = 'CPF e nascimento são obrigatórios (use Enriquecer).';
    return;
  }
  buscando.value = true;
  erroGeral.value = '';
  // Só chama os bancos ATIVOS no cofre (banco desativado não faz nenhuma
  // chamada). V8 não entra aqui — ele tem fluxo próprio (autorizar/consultar).
  const jobs = [];
  jobs.push(bancoAtivo('uy3') ? buscarUY3() : Promise.resolve(null));
  jobs.push(bancoAtivo('facta') ? buscarFACTA() : Promise.resolve(null));
  jobs.push(bancoAtivo('c6') ? buscarC6() : Promise.resolve(null));
  jobs.push(bancoAtivo('soma') ? buscarSOMA() : Promise.resolve(null));
  jobs.push(bancoAtivo('uy3') ? buscarMargemUY3() : Promise.resolve(null));
  const [uy3Err, , , , margemUy3] = await Promise.all(jobs);
  if (uy3Err) erroGeral.value = `UY3: ${uy3Err}`;
  // v107: monta os vínculos e resolve a seleção — mantém a escolha anterior
  // se ela ainda existir; com UM único vínculo ligado, seleciona sozinho.
  vinculos.value = montarVinculos(margemUy3);
  const ligados = vinculos.value.filter(v => v.ligado);
  if (!ligados.some(v => v.matricula === vinculoSel.value)) {
    vinculoSel.value = ligados.length === 1 ? ligados[0].matricula : '';
  }
  buscando.value = false;
};

// melhor do cliente (maior valor liquido entre TODOS os bancos, em reais)
const melhorValor = computed(() => {
  const vals = [];
  (uy3Offers.value || []).forEach(x => vals.push((x.o.liquidValue || 0) / 100));
  (factaState.value?.ofertas || []).forEach(o => vals.push(Number(o.valor_liquido) || 0));
  // v122: C6 concorre pela OFERTA MÁXIMA (valor_cliente do c6/oferta)
  if (c6Sim.value?.oferta) vals.push(Number(c6Sim.value.oferta.valor_cliente) || 0);
  // v125: SOMA concorre pelo melhor valor líquido das simulações
  (somaSim.value?.sims || []).forEach(s2 => vals.push(Number(s2.simValorLiquido) || 0));
  // V8: concorre pelo maior líquido do pre-calculate (e pela simulação, se houver)
  (v8Offers.value || []).forEach(o => vals.push(numBR(o.disbursementMaxValue)));
  if (v8Sim.value) vals.push(Number(v8Sim.value.disbursement_amount) || 0);
  return vals.length ? Math.max(...vals) : 0;
});
const isMelhor = valorReais =>
  melhorValor.value > 0 && Math.abs(valorReais - melhorValor.value) < 0.005;

// ----------------------------------------------------------- re-simular ----
const uy3Resim = ref({}); // idx -> {modo, valor, busy}
const c6Resim = ref({ tipo: 'POR_VALOR_PARCELA', valor: '', prazo: '24', busy: false });
const factaResim = ref({}); // idx -> {parcela, busy}

const resimUY3 = async idx => {
  const st = uy3Resim.value[idx];
  const card = uy3Offers.value[idx];
  st.erro = '';
  const valor = numBR(st.valor); // v140: aceita mascara R$
  if (!valor || valor <= 0) {
    st.erro = 'Informe o valor para simular (ex: 2000,00).';
    return;
  }
  st.busy = true;
  try {
    const o = card.o;
    const { data } = await cltApi.post('uy3/amortizar', {
      legalPerson: false,
      productId: o.productId,
      amortization: {
        agentCommission: { amount: 0, type: 'Percentage', baseValue: 'InitialValue' },
        apr: o.apr,
        startDate: o.startDate,
        requestedAmount: Math.round(valor * 100),
        amortizationType: o.amortizationType || 'price',
        calculateByValueType: st.modo,
        firstPaymentDate: o.firstPaymentDate,
        numberOfPayments: o.numberOfPayments,
        numberOfInterestPayments: o.numberOfInterestPayments || 0,
        calculationType: o.calculationType || 'V360Meses',
        paymentPeriodicity: o.paymentPeriodicity || { every: 1, periodicity: 'Monthly' },
        includePaymentFixedCosts: true,
        financeTaxExempted: false,
        dueDateOnBusinessDays: false,
      },
    });
    const r = data.amortization || data;
    if (r.liquidValue == null || r.code || r.error) {
      st.erro = `UY3: ${r.message || r.error || 'simulação inválida'}`;
      return;
    }
    const parcela = uy3ParcelaReal(r);
    card.o = { ...o, ...r, calculateByValueType: st.modo };
    card.parcelaReal = parcela;
    if (parcela) card.w = { ...card.w, totalValue: parcela / 100 };
    useAlert('UY3 re-simulado');
  } catch (e) {
    const d = e.response?.data || {};
    st.erro = `UY3: ${d.detail || d.message || d.error || e.message}`;
  } finally {
    st.busy = false;
  }
};

const resimC6 = async () => {
  const st = c6Resim.value;
  const valor = numBR(st.valor);
  const prazo = parseInt(st.prazo, 10);
  if (!valor || !prazo) {
    mostrarMsgC6('Informe o VALOR e o PRAZO para simular.');
    return;
  }
  st.busy = true;
  try {
    const body = { cpf: cpf.value, tipo_simulacao: st.tipo, prazo };
    if (st.tipo === 'POR_VALOR_SOLICITADO') body.valor_solicitado = valor;
    else body.valor_parcela = valor;
    const { data } = await cltApi.post('c6/simular', body);
    // v122: a simulação v2 devolve VÁRIAS condições (planos) — o operador
    // escolhe qual digitar. Shape antigo (objeto único) segue aceito.
    const conds = c6ExtraiCondicoes(data);
    if (conds.length) {
      c6Sim.value = { ...(c6Sim.value || {}), erro: null, condicoes: conds };
      useAlert(`C6: ${conds.length} condição(ões) disponível(is)`);
    } else useAlert(`C6: ${data.detail || data.error || 'simulação sem condições válidas'}`);
  } catch (e) {
    useAlert(`C6: ${e.response?.data?.detail || e.message}`);
  } finally {
    st.busy = false;
  }
};

// FACTA: etapa1 com nova parcela (valor = parcela / coeficiente)
const resimFACTA = async idx => {
  const st = factaResim.value[idx];
  const of = factaState.value.ofertas[idx];
  st.erro = '';
  const parcela = numBR(st.parcela); // v140: aceita mascara R$
  const coef = Number(of.coeficiente);
  if (!parcela) {
    st.erro = 'Informe o valor da parcela para simular.';
    return;
  }
  if (!coef) {
    st.erro = 'Oferta sem coeficiente — não dá pra re-simular esta tabela.';
    return;
  }
  st.busy = true;
  try {
    const valorOp = parcela / coef;
    const { data } = await cltApi.post('facta/etapa1-simulador', {
      produto: 'D',
      tipo_operacao: '13',
      averbador: '10010',
      convenio: '3',
      cpf: cpf.value,
      data_nascimento: ddmmyyyy(nascimento.value),
      prazo: String(of.prazo),
      codigo_tabela: String(of.codigoTabela),
      valor_operacao: valorOp.toFixed(2),
      valor_parcela: (Math.round(valorOp * coef * 100) / 100).toFixed(2),
      coeficiente: String(of.coeficiente),
      matricula: of.matricula || undefined,
    });
    const idSim = data.id_simulador || data.id || data.idSimulador;
    if (!idSim) {
      const extra = data.prestacao_maxima
        ? ` (parcela máx: ${data.prestacao_maxima})`
        : '';
      st.erro = `FACTA: ${data.mensagem || 'simulação recusada'}${extra}`;
      return;
    }
    of._idSimulador = idSim;
    of.parcela = parcela;
    of.contrato = valorOp;
    of.valor_liquido = valorOp; // aproximação até operacoes-disponiveis
    try {
      const vinc = (factaState.value.vinculos || []).find(
        v => String(v.matricula) === String(of.matricula)
      );
      const { data: ops } = await cltApi.get('facta/operacoes-disponiveis', {
        cpf: cpf.value,
        data_nascimento: ddmmyyyy(nascimento.value),
        valor_parcela: parcela.toFixed(2),
        valor_renda: Number(vinc?.valorTotalVencimentos || 0).toFixed(2),
        prazo: String(of.prazo),
        opcao_valor: '2',
        ...(of.matricula ? { matricula: of.matricula } : {}),
      });
      const lista = ops.tabelas || ops.opcoes || ops.operacoes || [];
      const real = lista.find(t => String(t.codigoTabela) === String(of.codigoTabela));
      if (real) {
        of.contrato = Number(real.contrato) || of.contrato;
        of.valor_liquido = Number(real.valor_liquido) || of.valor_liquido;
      }
    } catch (e) {
      // valores reais são best-effort; a simulação (id) já está garantida
    }
    useAlert('FACTA re-simulado');
  } catch (e) {
    const d = e.response?.data || {};
    st.erro = `FACTA: ${d.mensagem || d.detail || e.message}`;
  } finally {
    st.busy = false;
  }
};

// --------------------------------------------------------------- digitar ---
const view = ref('ofertas'); // ofertas | digitar | resultado
const dig = ref(null); // { banco, resumo:{valor,prazo,parcela,extra}, ctx }
const form = ref({});
const digBusy = ref(false);
const resultado = ref(null);

// v42: toda digitação que dá certo (resultado.ok) vira registro na tela
// Propostas (simulador-api). UM watch cobre os 6 pontos de sucesso (SOMA,
// UY3 com/sem link, FACTA, C6, V8) — e qualquer banco futuro que siga o
// padrão resultado.value = { banco, ok: true, numero }. O registro NUNCA
// pode quebrar a digitação: falhou, só loga.
watch(resultado, r => {
  if (!r || r.ok !== true) return;
  try {
    simuladorApi
      .post('propostas', {
        cpf: cpf.value,
        nome: nome.value,
        banco: String(r.banco || '').toLowerCase(),
        codigo: String(r.numero || ''),
        valor: numBR(dig.value?.resumo?.valor),
        produto: 'clt',
        situacao: 'Aguardando assinatura',
        payload: {
          link: r.link || null,
          msg: r.msg || '',
          resumo: dig.value?.resumo || null,
        },
      })
      .catch(() => {});
  } catch (e) {
    /* registro é acessório — a digitação segue */
  }
});

const _fakeEmail = n => {
  const p = (n || 'cliente').toLowerCase().normalize('NFD').replace(/[^a-z ]/g, '').split(' ').filter(Boolean);
  const base = (p[0] || 'cliente') + (p.length > 1 ? p[p.length - 1] : '');
  const nn = Math.floor(10 + Math.random() * 89);
  return `${base}${nn}@${Math.random() < 0.5 ? 'gmail' : 'hotmail'}.com`;
};
const _fakeHouseNumber = v =>
  v && v !== '0' ? v : String(100 + Math.floor(Math.random() * 400));

const baseForm = () => ({
  nome: nome.value,
  nascimento: nascimento.value,
  email: enrich.value.email || _fakeEmail(nome.value),
  telefone: onlyDigits(celular.value),
  cep: onlyDigits(enrich.value.postalCode || ''),
  rua: enrich.value.street || '',
  numero: _fakeHouseNumber(enrich.value.addressNumber || ''),
  complemento: '',
  bairro: enrich.value.neighborhood || '',
  cidade: enrich.value.city || '',
  uf: (enrich.value.state || '').toUpperCase(),
});

const abrirDigitarUY3 = async (idx) => {
  digBusy.value = true;
  try {
    const { data } = await cltApi.get('uy3/propostas-ativas', { cpf: cpf.value });
    if ((data.propostas || []).length) {
      useAlert('Este CPF já tem proposta UY3 em andamento — resolva no digitador antes.');
      return;
    }
  } catch (e) {
    // segue: o backend da proposta valida de novo
  } finally {
    digBusy.value = false;
  }
  const card = uy3Offers.value[idx];
  dig.value = {
    banco: 'UY3',
    ctx: card,
    resumo: {
      valor: cents(card.o.liquidValue),
      prazo: `${card.o.numberOfPayments}x`,
      parcela: card.parcelaReal ? cents(card.parcelaReal) : '—',
      extra: card.o.productName || '',
    },
  };
  form.value = {
    ...baseForm(),
    civilStatus: 'Single',
    occupation: 'Trabalhador',
    pixTipo: 'NaturalRegistrationNumber',
    pixChave: cpf.value,
  };
  view.value = 'digitar';
};

const abrirDigitarFACTA = async idx => {
  const of = factaState.value.ofertas[idx];
  if (!of._idSimulador) {
    // etapa1 com os valores atuais do card (gera o id_simulador)
    factaResim.value[idx] = factaResim.value[idx] || { parcela: of.parcela, busy: false };
    factaResim.value[idx].parcela = of.parcela;
    await resimFACTA(idx);
    if (!of._idSimulador) return;
  }
  dig.value = {
    banco: 'FACTA',
    ctx: of,
    resumo: {
      valor: brl(of.valor_liquido),
      prazo: `${of.prazo}x`,
      parcela: brl(of.parcela),
      extra: of.tabela || '',
    },
  };
  form.value = {
    ...baseForm(),
    sexo: 'M',
    estadoCivil: 'solteiro',
    nacionalidade: 'brasileira',
    nomeMae: enrich.value.motherName || '',
    rg: enrich.value.rgNumber || cpf.value,
    rgOrgao: enrich.value.rgIssuerOrg || 'SSP',
    rgUf: (enrich.value.rgIssuerUf || enrich.value.state || '').toUpperCase(),
    rgData: ddmmyyyy(enrich.value.rgIssueDate || '2023-04-05'),
    renda: '',
    matricula: of.matricula || '',
    pixTipo: '1',
    pixChave: cpf.value,
  };
  view.value = 'digitar';
};

// v135: Digitar na OFERTA MÁXIMA — auto-simula com o valor/prazo da oferta
// e mostra as condições pra escolher (a digitação é por condição/id_simulacao).
const digitarDaOfertaC6 = async () => {
  const o = c6Sim.value?.oferta;
  if (!o) return;
  c6Resim.value = {
    tipo: 'POR_VALOR_SOLICITADO',
    valor: String(o.valor_cliente),
    prazo: String(o.quantidade_parcelas || 24),
    busy: false,
  };
  mostrarMsgC6('Gerando condições da oferta máxima…');
  await resimC6();
};

// v122: recebe a CONDIÇÃO escolhida da lista da simulação v2.
const abrirDigitarC6 = c => {
  dig.value = {
    banco: 'C6',
    ctx: c,
    resumo: {
      valor: brl(numBR(c.valor_liquido) || numBR(c.valor_cliente)),
      prazo: `${c.quantidade_parcelas}x`,
      parcela: brl(numBR(c.valor_parcela)),
      extra: `IOF ${brl(numBR(c.valor_iof))} · ${c.convenio?.descricao || ''}`,
    },
  };
  form.value = {
    ...baseForm(),
    tipoConta: 'ContaCorrenteIndividual',
    numeroBanco: '',
    numeroAgencia: '',
    digitoAgencia: '',
    numeroConta: '',
    digitoConta: '',
  };
  view.value = 'digitar';
};

// ViaCEP direto do browser (igual o digitador faz)
const buscarCep = async () => {
  const c = onlyDigits(form.value.cep);
  if (c.length !== 8) return;
  try {
    const r = await fetch(`https://viacep.com.br/ws/${c}/json/`);
    const d = await r.json();
    if (d.erro) return;
    form.value.rua = d.logradouro || form.value.rua;
    form.value.bairro = d.bairro || form.value.bairro;
    form.value.cidade = d.localidade || form.value.cidade;
    form.value.uf = (d.uf || form.value.uf).toUpperCase();
    useAlert('Endereço preenchido pelo CEP');
  } catch (e) {
    // silencioso — usuário preenche na mão
  }
};

const etiquetarLink = () => {
  cltApi
    .post('chatwoot/etiquetar-link', { telefone: onlyDigits(celular.value) })
    .catch(() => {});
};

const ESTADO_CIVIL_FACTA = { solteiro: '1', casado: '2', divorciado: '3', viuvo: '4', separado: '5', uniao: '6' };
const NACIONALIDADE_FACTA = { brasileira: '1', naturalizada: '2', estrangeira: '3' };

const confirmarDigitacao = async () => {
  digBusy.value = true;
  erroGeral.value = '';
  try {
    if (dig.value.banco === 'UY3') await digitarUY3();
    else if (dig.value.banco === 'FACTA') await digitarFACTA();
    else if (dig.value.banco === 'SOMA') await digitarSOMA();
    else if (dig.value.banco === 'V8') await digitarV8();
    else await digitarC6();
  } catch (e) {
    const d = e.response?.data || {};
    erroGeral.value = `${dig.value.banco}: ${d.mensagem || d.detail || d.error || e.message}`;
  } finally {
    digBusy.value = false;
  }
};

const digitarUY3 = async () => {
  const f = form.value;
  const { o, w } = dig.value.ctx;
  // 1. pessoa
  const { data: p } = await cltApi.post('uy3/pessoa', {
    registrationNumber: cpf.value,
    name: f.nome,
    email: f.email,
    phone: onlyDigits(f.telefone),
    birthDate: `${f.nascimento}T00:00:00Z`,
    civilStatus: f.civilStatus,
    nationality: 'Brasileira',
    occupation: f.occupation || 'Trabalhador',
    address: {
      zipCode: onlyDigits(f.cep),
      addressName: f.rua,
      number: f.numero,
      complement: f.complemento || '',
      district: f.bairro,
      city: f.cidade,
      uf: (f.uf || '').toUpperCase(),
    },
  });
  // UY3 devolve o id como string COM aspas — mesmo tratamento do digitador antigo
  const personId =
    typeof p === 'string' ? p.replace(/"/g, '') : p.id || p.personId;
  if (!personId) throw new Error('UY3 não devolveu personId');
  // 2. conta (PIX)
  let keyPix = f.pixChave;
  if (f.pixTipo === 'NaturalRegistrationNumber') keyPix = cpf.value;
  if (f.pixTipo === 'Phone') keyPix = onlyDigits(keyPix);
  const { data: c } = await cltApi.post('uy3/conta', {
    personId,
    contas: [
      { operationTypeValue: 'Pix', pixKeyTypeValue: f.pixTipo, keyPix },
    ],
  });
  // UY3 devolve a conta como LISTA (uuid cru ou objeto) — igual o digitador antigo
  const contaItem = Array.isArray(c) ? c[0] : c;
  const bankAccountId =
    typeof contaItem === 'string'
      ? contaItem.replace(/"/g, '')
      : contaItem?.id || contaItem?.bankAccountId;
  if (!bankAccountId) throw new Error('UY3 não devolveu bankAccountId');
  // 3. proposta
  const { data: r } = await cltApi.post(
    'uy3/proposta',
    {
      productId: o.productId,
      personId,
      beneficiaryId: personId,
      bankAccountId,
      liquidationType: 'EletronicTransfer',
      amortization: {
        agentCommission: { amount: 0, type: 'Percentage', baseValue: 'InitialValue' },
        firstPaymentDate: o.firstPaymentDate,
        numberOfInterestPayments: o.numberOfInterestPayments || 0,
        apr: o.apr,
        startDate: o.startDate,
        requestedAmount: o.requestedAmount,
        amortizationType: o.amortizationType || 'price',
        calculateByValueType: o.calculateByValueType || 'Payment',
        numberOfPayments: o.numberOfPayments,
        calculationType: o.calculationType || 'V360Meses',
        paymentPeriodicity: o.paymentPeriodicity || { every: 1, periodicity: 'Monthly' },
      },
      warranty: [
        {
          warrantyType: 'DataprevEmployee',
          productId: o.productId,
          dataprev_EmployerName: w.dataprev_EmployerName,
          dataprev_EmployerRegistrationNumber: w.dataprev_EmployerRegistrationNumber,
          dataprev_EmployerRegistrationCode: w.dataprev_EmployerRegistrationCode,
          dataprev_EmployeeCode: w.dataprev_EmployeeCode,
          dataprev_DiscountStartPeriod: w.dataprev_DiscountStartPeriod,
          totalValue: Math.round((w.totalValue || 0) * 100),
          dataprev_EmployeeAdmissionDate: w.dataprev_EmployeeAdmissionDate,
          datasetId: w.datasetId,
        },
      ],
    },
    {
      'X-Cpf': cpf.value,
      'X-Telefone': onlyDigits(f.telefone),
      // v115: sinaliza LEILÃO pro backend travar (produto leilão só p/ CPF na
      // leilao_ctps). Header booleano ASCII pra evitar bug de acento (LEILÃO)
      // em header HTTP — mesma regex do badge visual.
      'X-Leilao': /LEIL[AÃ]O/i.test(o.productName || '') ? '1' : '0',
    }
  );
  const signUrl = extraiUrl(r.signUrl);
  if (signUrl) {
    etiquetarLink();
    resultado.value = {
      banco: 'UY3',
      ok: true,
      numero: r.creditNoteId,
      link: signUrl,
      msg: 'Proposta digitada — link de assinatura pronto.',
    };
  } else {
    // link ainda não saiu: registra pendência (o digitador envia sozinho)
    cltApi
      .post('uy3/link-pendente', {
        creditNoteId: r.creditNoteId,
        cpf: cpf.value,
        telefone: onlyDigits(f.telefone),
        valorLiquido: o.requestedAmount,
        valorParcela: dig.value.ctx.parcelaReal || null,
        prazo: o.numberOfPayments,
        usuario: null,
      })
      .catch(() => {});
    resultado.value = {
      banco: 'UY3',
      ok: true,
      numero: r.creditNoteId,
      link: null,
      msg: 'Proposta digitada! O link de assinatura será enviado automaticamente na conversa assim que o banco liberar.',
    };
  }
  view.value = 'resultado';
};

const digitarFACTA = async () => {
  const f = form.value;
  const of = dig.value.ctx;
  // cidade -> código FACTA
  const cidadeCod = await factaCidadeCodigo(f.uf, f.cidade);
  const { data: e2 } = await cltApi.post('facta/etapa2-dados-pessoais', {
    id_simulador: Number(of._idSimulador),
    cpf: cpf.value,
    nome: f.nome,
    sexo: f.sexo,
    estado_civil: ESTADO_CIVIL_FACTA[f.estadoCivil] || '1',
    data_nascimento: ddmmyyyy(f.nascimento),
    rg: f.rg,
    estado_rg: f.rgUf,
    orgao_emissor: f.rgOrgao,
    data_expedicao: f.rgData,
    estado_natural: f.uf,
    cidade_natural: cidadeCod,
    nacionalidade: NACIONALIDADE_FACTA[f.nacionalidade] || '1',
    celular: fmtCelularFacta(f.telefone),
    email: f.email,
    renda: Number(String(f.renda || '0').replace(',', '.')).toFixed(2),
    cep: onlyDigits(f.cep),
    endereco: f.rua,
    numero: f.numero,
    bairro: f.bairro,
    cidade: cidadeCod,
    estado: f.uf,
    nome_mae: f.nomeMae,
    nome_pai: 'NAO DECLARADO',
    matricula: String(f.matricula || ''),
    tipo_chave_pix: f.pixTipo,
    chave_pix: f.pixChave,
    cliente_iletrado_impossibilitado: 'N',
  });
  const codigoCliente = e2.codigo_cliente || e2.codigoCliente || e2.codigo;
  if (!codigoCliente)
    throw new Error(e2.mensagem || e2.erro || 'FACTA etapa2 sem codigo_cliente');
  const { data: e3 } = await cltApi.post('facta/etapa3-proposta', {
    codigo_cliente: Number(codigoCliente),
    id_simulador: Number(of._idSimulador),
    tipo_formalizacao: 'DIG',
  });
  const codigoAf = e3.codigo || e3.codigo_af || e3.id;
  const link = e3.url_formalizacao || e3.urlFormalizacao || e3.link;
  if (link) etiquetarLink();
  resultado.value = {
    banco: 'FACTA',
    ok: true,
    numero: codigoAf,
    link: link || null,
    msg: link
      ? 'Proposta digitada — link de formalização pronto.'
      : 'Proposta digitada (AF gerado).',
  };
  view.value = 'resultado';
};

const digitarC6 = async () => {
  const f = form.value;
  const tel = onlyDigits(f.telefone);
  const tel11 = tel.length === 10 ? `${tel.slice(0, 2)}9${tel.slice(2)}` : tel;
  const { data: r } = await cltApi.post('c6/incluir-proposta', {
    id_simulacao: String(dig.value.ctx.id_simulacao),
    cpf: cpf.value,
    ddd: tel11.slice(0, 2),
    numero_telefone: tel11.slice(2),
    logradouro: f.rua,
    numero: f.numero,
    cep: onlyDigits(f.cep),
    bairro: f.bairro,
    cidade: f.cidade,
    uf: (f.uf || '').toUpperCase(),
    dados_bancarios: {
      tipo_conta: f.tipoConta,
      numero_banco: onlyDigits(f.numeroBanco),
      numero_agencia: onlyDigits(f.numeroAgencia),
      digito_agencia: f.digitoAgencia || '',
      numero_conta: onlyDigits(f.numeroConta),
      digito_conta: f.digitoConta || '',
    },
  });
  const numero = r.numero_proposta || r.numeroProposta || r.proposta;
  if (!numero) throw new Error(r.detail || r.mensagem || 'C6 sem numero_proposta');
  let link = null;
  try {
    const { data: fm } = await cltApi.get(`c6/formalizacao/${numero}`);
    link = extraiUrl(fm.url || fm.link || fm);
  } catch (e) {
    // formalização pode demorar — não é erro
  }
  if (link) etiquetarLink();
  resultado.value = {
    banco: 'C6',
    ok: true,
    numero,
    link,
    msg: link
      ? 'Proposta digitada — link de formalização pronto.'
      : 'Proposta digitada! O link de formalização fica disponível em instantes.',
  };
  view.value = 'resultado';
};

const factaCidadeCodigo = async (uf, cidade) => {
  const norm = s =>
    String(s || '')
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  const { data } = await cltApi.get('facta/cidade', {
    estado: (uf || '').toUpperCase(),
    nome_cidade: norm(cidade),
  });
  const mapa = data.cidade || data.cidades || {};
  const alvo = norm(cidade);
  const hit = Object.entries(mapa).find(([, v]) => norm(v?.nome || v) === alvo);
  if (!hit) throw new Error(`Cidade "${cidade}/${uf}" não encontrada na FACTA`);
  return hit[0];
};

const fmtCelularFacta = tel => {
  const d = onlyDigits(tel);
  const n = d.length === 10 ? `${d.slice(0, 2)}9${d.slice(2)}` : d;
  return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`;
};

const extraiUrl = v => {
  if (!v) return null;
  if (typeof v === 'string') {
    const s = v.trim().replace(/^"|"$/g, '');
    return s.startsWith('http') ? s : null;
  }
  if (Array.isArray(v)) {
    for (const it of v) {
      const u = extraiUrl(it?.url || it);
      if (u) return u;
    }
    return null;
  }
  return extraiUrl(v.url || v.signUrl || v.link);
};

const voltarOfertas = () => {
  clearInterval(propostasTimer);
  propostasTimer = null;
  view.value = 'ofertas';
  dig.value = null;
  erroGeral.value = '';
};

// ----------------------------------------------- consultar propostas (v94) --
// Porta a tela "Status de Proposta" do digitador antigo pra dentro do modal:
// propostas-cliente (4 bancos em paralelo) + AO VIVO (refresh 60s) + ações
// Copiar link / Buscar link / Reapresentar PIX / Cancelar (backend só id 1).
const propostas = ref(null); // { uy3, facta, c6, soma }
const propostasBusy = ref(false);
const reapr = ref({}); // creditNoteId -> { open, tipo, chave, busy }
let propostasTimer = null;

const carregarPropostas = async () => {
  if (!cpfOk.value) return;
  propostasBusy.value = true;
  try {
    const { data } = await cltApi.get('propostas-cliente', { cpf: cpf.value });
    propostas.value = data;
  } catch (e) {
    erroGeral.value = `Propostas: ${e.response?.data?.detail || e.message}`;
  } finally {
    propostasBusy.value = false;
  }
};

const abrirPropostas = () => {
  if (!cpfOk.value) {
    erroGeral.value = 'Informe o CPF para consultar as propostas.';
    return;
  }
  erroGeral.value = '';
  propostas.value = null;
  view.value = 'propostas';
  carregarPropostas();
  clearInterval(propostasTimer);
  propostasTimer = setInterval(carregarPropostas, 60000);
};

const fecharPropostas = () => {
  clearInterval(propostasTimer);
  propostasTimer = null;
  voltarOfertas();
};

onBeforeUnmount(() => clearInterval(propostasTimer));

const statusCls = st => {
  const s = String(st || '').toUpperCase();
  if (/PAID|DISBURSED|FUNDED|LIQUIDAD|CONCLUID|FINISHED|COMPLET/.test(s)) return 'ok';
  if (/FORMAL|SIGNAT|COLETA|SIGNING/.test(s)) return 'info';
  if (/REJECT|CANCEL|FAIL|EXPIR|ERROR/.test(s)) return 'err';
  return 'pending';
};
const STATUS_BADGE = {
  ok: 'bg-n-teal-9 text-white',
  info: 'bg-n-blue-9 text-white',
  err: 'bg-n-ruby-9 text-white',
  pending: 'bg-n-amber-9 text-black-900',
};
const fmtData = iso => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const UY3_CANCEL_RE = /Cancel|Reject|Expir|Fail/i;
const uy3Items = computed(() => {
  const p = propostas.value?.uy3;
  const items = Array.isArray(p) ? p : [];
  // canceladas por último; com link primeiro (mesma ordem do digitador antigo)
  return [...items].sort((a, b) => {
    const ca = UY3_CANCEL_RE.test(String(a.status || '')) ? 1 : 0;
    const cb = UY3_CANCEL_RE.test(String(b.status || '')) ? 1 : 0;
    if (ca !== cb) return ca - cb;
    return (extraiUrl(a.__signUrl) ? 0 : 1) - (extraiUrl(b.__signUrl) ? 0 : 1);
  });
});
const factaItems = computed(() => {
  const p = propostas.value?.facta;
  if (Array.isArray(p)) return p;
  if (p && Array.isArray(p.propostas)) return p.propostas;
  return [];
});
const c6Items = computed(() =>
  Array.isArray(propostas.value?.c6) ? propostas.value.c6 : []
);
const somaItems = computed(() =>
  Array.isArray(propostas.value?.soma) ? propostas.value.soma : []
);
const v8Items = computed(() =>
  Array.isArray(propostas.value?.v8) ? propostas.value.v8 : []
);
const bancoErro = key => {
  const p = propostas.value?.[key];
  return p && !Array.isArray(p) ? p.error || '' : '';
};

const isRevisaoPix = st => {
  const s = String(st || '').toLowerCase();
  return (s.includes('revis') && s.includes('pagamento')) || s.includes('paymentrevision');
};
const uy3Finalizada = p =>
  /PAID|PAGO|LIQUIDAD|CONCLU|FINISH|ENCERRAD|COMPLET|CANCEL|REJECT|REPROV|DISAPPROV|EXPIR|FAIL/i.test(
    `${p.__resumo?.status || p.status || ''} ${p.__resumo?.statusDisplay || p.statusDisplay || ''}`
  );

const buscarLinkUY3 = async p => {
  try {
    const { data } = await cltApi.get(`uy3/signurl/${p.id}`);
    if (extraiUrl(data)) {
      p.__signUrl = data;
      useAlert('Link de assinatura disponível');
    } else {
      useAlert('UY3 ainda não gerou o link (biometria pendente).');
    }
  } catch (e) {
    useAlert(`UY3: ${e.response?.data?.detail || e.message}`);
  }
};

const abrirReapr = p => {
  reapr.value[p.id] = { open: true, tipo: 'NaturalRegistrationNumber', chave: cpf.value, busy: false };
};
const ajustaChaveReapr = id => {
  const st = reapr.value[id];
  st.chave = st.tipo === 'NaturalRegistrationNumber' ? cpf.value : '';
};
const submitReapr = async id => {
  const st = reapr.value[id];
  let chave = String(st.chave || '').trim();
  if (st.tipo === 'NaturalRegistrationNumber') chave = cpf.value;
  if (st.tipo === 'Phone') chave = onlyDigits(chave);
  if (!chave) {
    useAlert('Informe a chave PIX');
    return;
  }
  st.busy = true;
  try {
    await cltApi.post('uy3/reapresentar-pagamento', {
      creditNoteId: id,
      pixKeyTypeValue: st.tipo,
      keyPix: chave,
    });
    useAlert('Pagamento reapresentado com sucesso');
    st.open = false;
    carregarPropostas();
  } catch (e) {
    useAlert(`Erro ao reapresentar: ${e.response?.data?.detail || e.message}`);
  } finally {
    st.busy = false;
  }
};

const cancelarUY3 = async p => {
  const label = p.creditNoteNo || p.id;
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Tem certeza que deseja CANCELAR a proposta ${label}?\n\nEsta ação é IRREVERSÍVEL.`)) return;
  try {
    await cltApi.post('uy3/cancelar-proposta', { creditNoteId: p.id });
    useAlert('Proposta cancelada com sucesso');
    carregarPropostas();
  } catch (e) {
    useAlert(
      e.response?.status === 403
        ? 'Apenas o administrador (id 1) pode cancelar propostas.'
        : `Erro ao cancelar: ${e.response?.data?.detail || e.message}`
    );
  }
};

// ==================== V8 (Fase 2) ====================
// A "autorização" do V8 (via multi/autorizar) cria o consult e autoriza o termo
// server-side; aqui o card faz polling do estado/margem, busca ofertas por prazo,
// simula (por parcela OU valor líquido, com/sem seguro) e gera a proposta.
const v8Consult = ref(null);
const v8Estado = ref(null); // {status, margem, installmentsMin/Max, valueMin/Max, employer..., raw}
const v8Offers = ref(null); // [{installmentNumbers, maxInstallmentValue, disbursementMaxValue}]
const v8Sim = ref(null); // resultado da simulação REAL (tem id_simulation)
const v8Prop = ref(null); // {id, formalization_url}
const v8SelPrazo = ref(''); // prazo selecionado nos chips
const v8DigitarOpen = ref(false); // abre os campos PIX pra digitar
const v8Form = ref({
  modo: 'valor_liquido',
  valor: '',
  comSeguro: true,
  pixTipo: 'cpf',
  pixChave: '',
  busy: false,
});

// Oferta (pre-calculate) do prazo selecionado.
const v8OfertaSel = computed(() =>
  (v8Offers.value || []).find(
    o => String(o.installmentNumbers) === String(v8SelPrazo.value)
  ) || null
);
// Valores do CARD: se já simulou (v8Sim) usa o retorno REAL; senão usa o MÁXIMO
// do pre-calculate do prazo selecionado (preview — sem taxa/id, Digitar travado).
const v8CardLiquido = computed(() =>
  v8Sim.value ? Number(v8Sim.value.disbursement_amount) || 0 : numBR(v8OfertaSel.value?.disbursementMaxValue)
);
const v8CardParcela = computed(() =>
  v8Sim.value ? Number(v8Sim.value.installment_value) || 0 : numBR(v8OfertaSel.value?.maxInstallmentValue)
);
const v8CardPrazo = computed(() =>
  v8Sim.value ? v8Sim.value.number_of_installments : v8SelPrazo.value
);
const v8CardTaxa = computed(() =>
  v8Sim.value?.monthly_interest_rate != null
    ? `${(Number(v8Sim.value.monthly_interest_rate) * 100).toFixed(2)}%`
    : '—'
);
const v8Melhor = computed(() => isMelhor(v8CardLiquido.value));
let v8Timer = null;
let v8Polls = 0;
const v8Busy = ref(false);
const v8Cooldown = ref(0); // segundos de espera pós rate-limit (429) da V8
let v8CdTimer = null;

const iniciarV8Cooldown = secs => {
  v8Cooldown.value = secs;
  if (v8CdTimer) clearInterval(v8CdTimer);
  v8CdTimer = setInterval(() => {
    v8Cooldown.value -= 1;
    if (v8Cooldown.value <= 0) {
      clearInterval(v8CdTimer);
      v8CdTimer = null;
    }
  }, 1000);
};

// "consultando" = ocupado, em cooldown de rate-limit, ou status ainda em análise.
// É SÓ nesse estado que o botão Consultar aparece (padrão do SOMA); quando vem
// retorno (SUCCESS ou terminal tipo REJECTED/CONSENT_APPROVED) o botão some.
const v8Consultando = computed(() => {
  if (v8Busy.value || v8Cooldown.value > 0) return true;
  return /WAITING|PENDING|PROCESS|ANALYS|ANDAMENTO|CONSENT|RECEIVED|CREATED/i.test(
    String(v8Estado.value?.status || '')
  );
});

// Botão ↻ do card V8 (igual ↻ Atualizar do SOMA): se JÁ existe consult, só
// RE-BUSCA o estado dele (não re-autoriza — re-autorizar dá 400 "já existe
// consult"). Sem consult ainda, aí sim autoriza um novo.
const atualizarV8 = () => {
  if (v8Busy.value || v8Cooldown.value > 0) return;
  if (v8Consult.value) {
    pararV8Poll();
    buscarV8Estado();
  } else {
    consultarV8();
  }
};

// Cria o consult + autoriza o termo server-side e começa o polling da margem
// (usado quando ainda não há consult; o "Autorizar bancos" também dispara isto).
const consultarV8 = async () => {
  if (v8Busy.value) return;
  if (!cpfOk.value || !nome.value || !celular.value || !nascimento.value) {
    useAlert('Preencha CPF, nome, celular e nascimento (use Enriquecer).');
    return;
  }
  v8Busy.value = true;
  pararV8Poll();
  v8Estado.value = { status: 'WAITING_CREDIT_ANALYSIS' };
  v8Offers.value = null;
  v8Sim.value = null;
  v8Prop.value = null;
  v8SelPrazo.value = '';
  v8DigitarOpen.value = false;
  try {
    const { data } = await cltApi.post('v8/autorizar', {
      cpf: cpf.value,
      nome: nome.value,
      celular: onlyDigits(celular.value),
      data_nascimento: nascimento.value,
    });
    if (data?.consult_id) {
      v8Consult.value = data.consult_id;
      v8Estado.value = { status: data.status || 'WAITING_CREDIT_ANALYSIS' };
      buscarV8Estado();
    } else {
      v8Estado.value = { status: 'ERROR', erro: data?.error || 'Falha ao autorizar V8' };
      useAlert(data?.error || 'V8: falha ao autorizar.');
    }
  } catch (e) {
    if (e.response?.status === 429) {
      iniciarV8Cooldown(12);
      v8Estado.value = { status: 'WAITING_CREDIT_ANALYSIS' };
      v8Timer = setTimeout(() => buscarV8Estado(true), 12000);
    } else {
      v8Estado.value = { status: 'ERROR', erro: e.response?.data?.detail || e.message };
      useAlert(`Erro ao consultar V8: ${e.response?.data?.detail || e.message}`);
    }
  } finally {
    v8Busy.value = false;
  }
};

const pararV8Poll = () => {
  if (v8Timer) {
    clearTimeout(v8Timer);
    v8Timer = null;
  }
};

// Só continua o polling ENQUANTO a margem está sendo analisada. Qualquer
// status terminal (SUCCESS, REJECTED, ERROR, etc.) PARA o poll — evita ficar
// preso em "Consultando margem…". Cap de ~2min como rede de segurança.
const V8_AGUARDANDO = /WAITING|PENDING|PROCESS|ANALYS|IN_PROGRESS|ANDAMENTO|CONSENT|RECEIVED|CREATED/i;
const buscarV8Estado = async (auto = false) => {
  if (!v8Consult.value) return;
  if (!auto) v8Polls = 0;
  try {
    const { data } = await cltApi.get('v8/estado', {
      cpf: cpf.value,
      consult_id: v8Consult.value,
    });
    v8Estado.value = data;
    const st = String(data?.status || '');
    if (st === 'SUCCESS') {
      pararV8Poll();
      carregarV8Offers();
    } else if (V8_AGUARDANDO.test(st) && (v8Polls += 1) < 60) {
      v8Timer = setTimeout(() => buscarV8Estado(true), 3000);
    } else {
      pararV8Poll(); // terminal (REJECTED/ERROR) ou timeout — para e mostra status
    }
  } catch (e) {
    if (e.response?.status === 429) {
      // Rate limit da V8: mostra o botão + espera 12s e re-tenta sozinho.
      iniciarV8Cooldown(12);
      pararV8Poll();
      v8Timer = setTimeout(() => buscarV8Estado(true), 12000);
      return;
    }
    pararV8Poll();
  }
};

const v8SimErro = ref('');
// A dica "digite um valor menor" era colada em QUALQUER erro da V8 —
// inclusive em tabela expirada, onde baixar o valor nunca resolveria e
// só fazia o operador perder tempo. Agora ela só aparece quando o erro
// é mesmo de valor/limite.
const erroEhDeValor = computed(() =>
  /valor|limite|m[íi]nimo|m[áa]ximo|amount|installment/i.test(v8SimErro.value || '')
);

// Carrega ofertas + a simulação do prazo (feita no BACKEND, já com id/taxa/
// bancarizadora e anti-422). Opcional `prazo` = o chip clicado; sem ele, o
// backend simula o de MAIOR valor líquido. O card mostra na hora, sem corrida.
const carregarV8Offers = async (prazo = null) => {
  if (!v8Consult.value || v8Form.value.busy) return;
  v8Form.value.busy = true;
  v8SimErro.value = '';
  try {
    const { data } = await cltApi.post('v8/ofertas', {
      cpf: cpf.value,
      consult_id: v8Consult.value,
      com_seguro: v8Form.value.comSeguro,
      prazo,
    });
    v8Offers.value = Array.isArray(data?.installments) ? data.installments : [];
    if (data?.prazo) v8SelPrazo.value = data.prazo;
    if (data?.simulacao?.id_simulation) {
      v8Sim.value = data.simulacao;
    } else {
      v8Sim.value = null;
      v8SimErro.value = data?.sim_erro || '';
    }
  } catch (e) {
    v8Offers.value = v8Offers.value || [];
  } finally {
    v8Form.value.busy = false;
  }
};

// Clicar num prazo: re-consulta as ofertas mirando aquele prazo (o backend
// simula e devolve id/taxa/valor daquele prazo).
const selecionarPrazoV8 = pz => {
  v8SelPrazo.value = pz;
  v8DigitarOpen.value = false;
  carregarV8Offers(pz);
};

// "Digitar esta" (só depois de simular): abre a VIEW de digitação com o
// formulário completo e editável — mesmo padrão de UY3/FACTA/C6/SOMA.
const abrirDigitarV8 = () => {
  const sim = v8Sim.value;
  if (!sim?.id_simulation) return;
  dig.value = {
    banco: 'V8',
    ctx: { sim, consulta: v8Estado.value?.raw || {}, consult_id: v8Consult.value },
    resumo: {
      valor: brl(v8CardLiquido.value),
      prazo: `${v8CardPrazo.value}x`,
      parcela: brl(v8CardParcela.value),
      extra: `${sim.provider ? sim.provider + ' · ' : ''}IOF ${brl(sim.iof_amount)} · taxa ${v8CardTaxa.value}`,
    },
  };
  form.value = {
    ...baseForm(),
    sexo: 'M',
    nomeMae: v8Estado.value?.motherName || enrich.value.motherName || '',
    pixTipo: 'cpf',
    pixChave: cpf.value,
  };
  view.value = 'digitar';
};

const digitarV8 = async () => {
  const f = form.value;
  const sim = dig.value.ctx.sim || {};
  const { data } = await cltApi.post('v8/proposta', {
    cpf: cpf.value,
    nome: f.nome,
    email: f.email,
    celular: onlyDigits(f.telefone),
    data_nascimento: f.nascimento,
    nome_mae: f.nomeMae,
    genero: f.sexo === 'F' ? 'female' : 'male',
    cep: onlyDigits(f.cep),
    cidade: f.cidade,
    uf: f.uf,
    numero: f.numero,
    rua: f.rua,
    complemento: f.complemento,
    bairro: f.bairro,
    simulation_id: sim.id_simulation,
    consult_id: dig.value.ctx.consult_id,
    consulta: dig.value.ctx.consulta,
    pix_key: f.pixChave,
    pix_key_type: f.pixTipo,
  });
  if (!data?.id) throw new Error(data?.error || data?.detail || 'V8: proposta sem id.');
  v8Prop.value = data;
  resultado.value = {
    banco: 'V8',
    ok: true,
    numero: data.id,
    link: data.formalization_url || null,
    msg: 'Proposta V8 gerada com sucesso.',
  };
  view.value = 'resultado';
};

const simularV8 = async () => {
  if (v8Form.value.busy) return;
  const prazo = Number(v8SelPrazo.value);
  if (!prazo) {
    useAlert('Selecione um prazo.');
    return;
  }
  const valor = numBR(v8Form.value.valor);
  // Vazio = simular o MÁXIMO do prazo pelo backend (anti-422, salva sozinho).
  if (!valor) {
    carregarV8Offers(v8SelPrazo.value);
    return;
  }
  const modo = v8Form.value.modo;
  v8Form.value.busy = true;
  try {
    const payload = {
      cpf: cpf.value,
      consult_id: v8Consult.value,
      number_of_installments: prazo,
      com_seguro: v8Form.value.comSeguro,
      modo,
    };
    if (modo === 'valor_liquido') payload.disbursed_amount = valor;
    else payload.installment_face_value = valor;
    const { data } = await cltApi.post('v8/simular', payload);
    if (data?.id_simulation) {
      v8Sim.value = data;
      v8DigitarOpen.value = false; // libera o Digitar (agora tem id)
    } else {
      useAlert(data?.error || 'V8: simulação sem retorno.');
    }
  } catch (e) {
    useAlert(`Erro ao simular V8: ${e.response?.data?.detail || e.message}`);
  } finally {
    v8Form.value.busy = false;
  }
};

const gerarPropostaV8 = async () => {
  if (!v8Sim.value?.id_simulation || v8Form.value.busy) return;
  if (!v8Form.value.pixChave) {
    useAlert('Informe a chave PIX.');
    return;
  }
  v8Form.value.busy = true;
  try {
    const { data } = await cltApi.post('v8/proposta', {
      cpf: cpf.value,
      nome: nome.value,
      celular: onlyDigits(celular.value),
      data_nascimento: nascimento.value,
      simulation_id: v8Sim.value.id_simulation,
      consult_id: v8Consult.value,
      consulta: v8Estado.value?.raw || {},
      pix_key: v8Form.value.pixChave,
      pix_key_type: v8Form.value.pixTipo,
    });
    if (data?.id) {
      v8Prop.value = data;
    } else {
      useAlert(data?.error || 'V8: proposta sem id.');
    }
  } catch (e) {
    useAlert(`Erro ao gerar proposta V8: ${e.response?.data?.detail || e.message}`);
  } finally {
    v8Form.value.busy = false;
  }
};

// Quando a autorização (multi/autorizar) devolve o consult_id do V8, começa o poll.
watch(
  () => auth.value?.v8,
  v => {
    const cid = v?.consult_id;
    if (cid && cid !== v8Consult.value) {
      v8Consult.value = cid;
      v8Estado.value = { status: v.status || 'WAITING_CREDIT_ANALYSIS' };
      v8Offers.value = null;
      v8Sim.value = null;
      v8Prop.value = null;
      v8SelPrazo.value = '';
      v8DigitarOpen.value = false;
      buscarV8Estado();
    }
  },
  { deep: true }
);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div
    class="flex fixed inset-0 z-50 justify-center items-start p-4 bg-black-900/70"
    @click.self="emit('close')"
  >
    <!-- v94: borda verde + fundo verde-escuro pra destacar do sistema atrás -->
    <div
      class="flex flex-col w-full max-h-[94vh] mt-2 rounded-xl border-2 shadow-2xl border-green-400 bg-green-900"
      :class="'w-[97vw] max-w-[120rem]'"
    >
      <!-- header (v100: padrão novo do Kairo — ícone verde no quadrado,
           título forte, nome do cliente em caixa alta) -->
      <div class="flex gap-3 items-center px-4 py-3 border-b border-n-weak">
        <span class="sim-ico"><span class="i-lucide-calculator size-4" /></span>
        <h3 class="text-base font-bold text-white">
          Simulador CLT
          <span class="text-sm font-normal uppercase text-n-slate-11">· {{ nome || 'cliente' }}</span>
        </h3>
        <span class="flex-1" />
        <button
          v-if="view !== 'ofertas'"
          type="button"
          class="px-3 py-1 text-xs font-semibold rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
          @click="voltarOfertas"
        >
          ← Voltar às ofertas
        </button>
        <button
          type="button"
          class="px-4 py-1.5 text-xs font-bold text-white bg-red-500 rounded-xl hover:bg-red-600"
          @click="emit('close')"
        >
          ✕ Fechar
        </button>
      </div>

      <div class="overflow-auto flex-1 p-4">
        <p v-if="erroGeral" class="p-2 mb-3 text-sm rounded-lg border border-n-ruby-7 bg-n-ruby-2 text-n-ruby-11">
          {{ erroGeral }}
        </p>

        <!-- ================= VIEW: OFERTAS ================= -->
        <template v-if="view === 'ofertas'">
          <!-- ficha (v100: padrão novo do Kairo — cartão escuro, ícones verdes,
               valores em negrito; os campos continuam EDITÁVEIS, só mudou o
               visual. Botões ganham ícone, mesmas cores/ações de antes.) -->
          <div class="p-3 mb-3 rounded-xl border border-n-weak bg-n-solid-1">
            <div class="ficha-grid">
              <label class="ficha-campo">
                <span class="ficha-label"><span class="i-lucide-circle-user size-4" /> CPF</span>
                <!-- v106: aceita CPF com ponto/traço e limpa sozinho (só
                     dígitos, máx 11). SEM maxlength: ele truncava o CPF
                     mascarado colado ANTES da limpeza. -->
                <input
                  :value="cpf"
                  placeholder="—"
                  class="ficha-input tabular-nums"
                  :class="{ 'ficha-input-erro': cpf && !cpfOk }"
                  @input="
                    cpf = onlyDigits($event.target.value).slice(0, 11);
                    $event.target.value = cpf;
                  "
                />
              </label>
              <label class="ficha-campo">
                <span class="ficha-label"><span class="i-lucide-user size-4" /> Nome</span>
                <input v-model="nome" placeholder="—" class="ficha-input" />
              </label>
              <label class="ficha-campo">
                <span class="ficha-label"><span class="i-lucide-calendar size-4" /> Nascimento</span>
                <input v-model="nascimento" type="date" class="ficha-input tabular-nums" />
              </label>
              <label class="ficha-campo">
                <span class="ficha-label">
                  <span class="i-lucide-smartphone size-4" /> Celular
                  <span
                    v-if="origemFone"
                    class="ml-1 px-1.5 py-0.5 rounded text-[10px] font-medium"
                    :class="FONE_SELO[origemFone].classe"
                    :title="FONE_SELO[origemFone].ajuda"
                  >
                    {{ FONE_SELO[origemFone].texto }}
                  </span>
                </span>
                <input
                  v-model="celular"
                  placeholder="—"
                  class="ficha-input tabular-nums"
                  @blur="arrumarCelular"
                />
                <span v-if="celularSuspeito" class="text-[10px] text-n-amber-11 mt-0.5">
                  Esperado DDD + 9 dígitos (ex.: 51989304770).
                </span>
              </label>
            </div>
            <div class="flex flex-wrap gap-4 justify-center items-center mt-4">
              <!-- v143: limpa SÓ os retornos dos bancos na tela (nada no BD) -->
              <button
                type="button"
                class="acao-btn text-white bg-n-ruby-9 hover:bg-n-ruby-10"
                @click="limparRetornos"
              >
                <span class="i-lucide-eraser size-4" />
                Limpar
              </button>
              <button
                type="button"
                class="acao-btn text-black-900 bg-yellow-400 hover:bg-yellow-500 disabled:opacity-50"
                :disabled="!cpfOk || busy === 'enriquecer'"
                @click="enriquecer"
              >
                <span class="i-lucide-user-plus size-4" />
                {{ busy === 'enriquecer' ? 'Enriquecendo…' : 'Enriquecer' }}
              </button>
              <button
                type="button"
                class="acao-btn text-white bg-n-blue-9 hover:bg-n-blue-10 disabled:opacity-50"
                :disabled="busy === 'autorizar'"
                @click="autorizar"
              >
                <span class="i-lucide-landmark size-4" />
                {{ busy === 'autorizar' ? 'Enviando…' : 'Autorizar bancos' }}
              </button>
              <button
                type="button"
                class="acao-btn acao-buscar text-white disabled:opacity-50"
                :disabled="buscando"
                @click="buscarOfertas"
              >
                <span class="i-lucide-search size-4" />
                {{ buscando ? 'Buscando…' : 'Buscar ofertas' }}
              </button>
              <button
                type="button"
                class="acao-btn text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50"
                :disabled="!cpfOk"
                @click="abrirPropostas"
              >
                <span class="i-lucide-file-text size-4" />
                Consultar propostas
              </button>
            </div>
          </div>

          <!-- estados de autorização (v93: branco negrito, fundo forte) -->
          <div v-if="auth" class="flex flex-wrap gap-2 items-center mb-3">
            <template v-for="b in bancosAtivos" :key="b">
              <span
                v-if="authState(b)"
                class="inline-flex gap-1.5 items-center px-3 py-1.5 text-[11px] font-bold text-white rounded-lg"
                :class="{
                  'bg-n-teal-9': authState(b).cls === 'ok' || authState(b).cls === 'roxo',
                  'bg-n-amber-9': authState(b).cls === 'aguarda' || authState(b).cls === 'expirado',
                  'bg-n-ruby-9': authState(b).cls === 'erro',
                }"
              >
                {{ b.toUpperCase() }} · {{ authState(b).label }}
              </span>
              <button
                v-if="authState(b) && authState(b).link"
                type="button"
                class="px-2.5 py-1.5 text-[11px] font-bold rounded-lg border border-n-teal-9 text-n-teal-11 hover:bg-n-teal-3"
                @click="copiar(authState(b).link, 'Link C6')"
              >
                <span class="i-lucide-clipboard size-3 inline-block align-[-1px]" />
                Copiar link
              </button>
              <!-- v122: link C6 expirado sem autorização — operador confirma gerar outro -->
              <button
                v-if="b === 'c6' && authState(b) && authState(b).cls === 'expirado'"
                type="button"
                :disabled="busy === 'c6novolink'"
                class="px-2.5 py-1.5 text-[11px] font-bold rounded-lg border border-n-amber-9 text-n-amber-11 hover:bg-n-amber-3"
                @click="gerarNovoLinkC6"
              >
                {{ busy === 'c6novolink' ? 'Gerando…' : 'Gerar novo link C6' }}
              </button>
            </template>
          </div>

          <!-- v139: janela de escolha quando JÁ existe consulta do dia -->
          <div
            v-if="autorizarDialog"
            class="flex fixed inset-0 z-[60] justify-center items-center bg-black-900/60"
            @click.self="autorizarDialog = false"
          >
            <div class="p-6 mx-4 w-full max-w-md text-center rounded-2xl border-2 shadow-2xl border-n-teal-9 bg-n-solid-1">
              <h3 class="text-base font-bold text-n-slate-12">
                Existe consulta para esse CPF, o que deseja fazer?
              </h3>
              <p class="mt-1 text-xs text-n-slate-11">
                CONTINUAR usa as consultas de HOJE já salvas (não gasta o
                limite dos bancos). NOVA CONSULTA refaz e salva por cima —
                nada é apagado.
              </p>
              <div class="flex gap-4 justify-center mt-5">
                <button
                  type="button"
                  class="px-5 py-2.5 text-sm font-bold text-white rounded-xl bg-n-iris-9 hover:opacity-90"
                  @click="executarAutorizar(true)"
                >
                  Nova consulta
                </button>
                <button
                  type="button"
                  class="px-5 py-2.5 text-sm font-bold text-white rounded-xl bg-n-teal-9 hover:opacity-90"
                  @click="executarAutorizar(false)"
                >
                  Continuar
                </button>
              </div>
            </div>
          </div>

          <!-- v107: VÍNCULOS EMPREGATÍCIOS — escolher ANTES das ofertas.
               AZUL = ligado/elegível (clicável) · VERMELHO = desligado (trava).
               Com 2+ vínculos, as ofertas só aparecem após a seleção; toda
               oferta/digitação fica amarrada à matrícula selecionada. -->
          <div v-if="vinculos && vinculos.length" class="mb-3">
            <div class="flex gap-2 items-center mb-2">
              <span class="i-lucide-briefcase size-4 text-n-slate-11" />
              <b class="text-sm text-n-slate-12">Vínculos empregatícios</b>
              <span class="text-[11px] text-n-slate-11">
                — selecione o vínculo do empréstimo
              </span>
            </div>
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <button
                v-for="v in vinculos"
                :key="v.matricula"
                type="button"
                class="vinc-card"
                :class="[
                  v.ligado ? 'ligado' : 'desligado',
                  { selecionado: vinculoSel === v.matricula },
                ]"
                :disabled="!v.ligado"
                @click="selecionarVinculo(v)"
              >
                <div class="vinc-topo">
                  <span class="vinc-status">{{ v.ligado ? 'LIGADO' : 'DESLIGADO' }}</span>
                  <span v-if="vinculoSel === v.matricula" class="vinc-check">
                    <span class="i-lucide-check size-3.5" /> SELECIONADO
                  </span>
                </div>
                <p class="vinc-empregador">{{ v.empregador || '—' }}</p>
                <p v-if="v.cnpj" class="vinc-linha">
                  CNPJ: <b class="tabular-nums">{{ v.cnpj }}</b>
                </p>
                <div class="vinc-info">
                  <div>
                    <span>Matrícula</span>
                    <b class="tabular-nums">{{ v.matricula }}</b>
                  </div>
                  <div>
                    <span>Admissão</span>
                    <b>{{ v.admissao ? ddmmyyyy(v.admissao) || v.admissao : '—' }}</b>
                  </div>
                  <div>
                    <span>Salário</span>
                    <b class="tabular-nums">{{ v.salario ? brl(v.salario) : '—' }}</b>
                  </div>
                  <div>
                    <span>Margem</span>
                    <b class="tabular-nums">{{ v.margem ? brl(v.margem) : '—' }}</b>
                  </div>
                </div>
                <p v-if="!v.ligado && v.motivo" class="vinc-motivo">{{ v.motivo }}</p>
                <p class="vinc-fontes">{{ v.fontes.join(' · ') }}</p>
              </button>
            </div>
            <p
              v-if="!mostrarOfertas"
              class="p-2.5 mt-3 text-xs font-semibold text-center rounded-lg border border-dashed border-n-weak text-n-slate-11"
            >
              👆 Selecione um vínculo LIGADO acima para ver as ofertas associadas a ele.
            </p>
          </div>

          <!-- colunas de ofertas (v107: só após vínculo resolvido) -->
          <!-- Grid PROPORCIONAL: auto-fit encaixa 2 a 6 bancos sem coluna
               fantasma (banco desativado no cofre nem entra via bancoAtivo). -->
          <div
            v-if="mostrarOfertas"
            class="grid gap-3"
            :style="{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }"
          >
            <!-- UY3 (v93: borda azul do banco) -->
            <div v-if="bancoAtivo('uy3')" class="overflow-hidden rounded-xl border-2 border-n-blue-9/70">
              <div class="flex gap-2 items-center px-3 py-2 border-b border-n-blue-9/40 bg-n-blue-9">
                <span class="bg-white rounded-sm size-2.5" />
                <b class="text-xs tracking-wider text-white">UY3</b>
                <span class="ml-auto text-[10px] uppercase text-white/80">
                  {{ uy3Offers ? `${uy3Offers.length} oferta(s)` : '—' }}
                </span>
              </div>
              <div class="flex flex-col gap-2 p-2.5">
                <p v-if="uy3Offers === null" class="py-4 text-xs text-center text-n-slate-11">
                  Busque as ofertas
                </p>
                <div v-else-if="!uy3Offers.length" class="p-3 text-[11px] rounded-lg border border-dashed border-n-ruby-7 text-n-ruby-11">
                  <b class="block text-[10px] uppercase">Não Elegível</b>
                  <p class="mt-1">{{ uy3Motivo || 'Nenhuma oferta UY3 para este CPF.' }}</p>
                </div>
                <!-- v100: card no padrão novo do Kairo (credito-card) -->
                <!-- v107: v-show amarra o card ao vínculo selecionado (matrícula
                     do warranty); v-show (e não v-if) preserva o índice do
                     uy3Resim -->
                <div
                  v-for="(card, idx) in uy3Offers || []"
                  v-show="uy3CardVisivel(card)"
                  :key="card.o.id || idx"
                  class="credito-card"
                  :class="{ melhor: isMelhor((card.o.liquidValue || 0) / 100) }"
                >
                  <div class="cc-header">
                    <div>
                      <span class="cc-label">Valor liberado</span>
                      <h2 class="cc-valor tabular-nums">{{ cents(card.o.liquidValue) }}</h2>
                    </div>
                    <div class="cc-badges">
                      <span v-if="isMelhor((card.o.liquidValue || 0) / 100)" class="cc-badge sucesso">
                        ★ Melhor do cliente
                      </span>
                      <span v-if="/LEIL[AÃ]O/i.test(card.o.productName || '')" class="cc-badge leilao">
                        Leilão
                      </span>
                    </div>
                  </div>
                  <div class="cc-info">
                    <div>
                      <span>Prazo</span>
                      <strong>{{ card.o.numberOfPayments }}x</strong>
                    </div>
                    <div>
                      <span>Parcela</span>
                      <strong class="tabular-nums">{{ cents(card.parcelaReal != null ? card.parcelaReal : card.o.requestedAmount) }}</strong>
                    </div>
                    <div>
                      <span>Produto</span>
                      <strong>{{ uy3Produto(card.o.productName) }}</strong>
                    </div>
                  </div>
                  <div class="cc-id">
                    ID da simulação: <b class="tabular-nums">{{ card.o.id || '—' }}</b>
                  </div>
                  <div class="cc-actions">
                    <button
                      type="button"
                      class="cc-btn"
                      @click="
                        copiarOferta({
                          valor: cents(card.o.liquidValue),
                          prazo: card.o.numberOfPayments,
                          parcela: cents(card.parcelaReal != null ? card.parcelaReal : card.o.requestedAmount),
                        })
                      "
                    >
                      Copiar
                    </button>
                    <button
                      type="button"
                      class="cc-btn digitar"
                      :disabled="digBusy"
                      @click="abrirDigitarUY3(idx)"
                    >
                      Digitar esta
                    </button>
                  </div>
                  <p v-if="uy3Resim[idx] && uy3Resim[idx].erro" class="cc-erro">
                    {{ uy3Resim[idx].erro }}
                  </p>
                  <div v-if="uy3Resim[idx]" class="cc-simbox">
                    <label>Simular novo valor</label>
                    <div class="cc-toggle">
                      <button
                        type="button"
                        :class="{ on: uy3Resim[idx].modo === 'Liquid' }"
                        @click="uy3Resim[idx].modo = 'Liquid'"
                      >
                        Valor líquido
                      </button>
                      <button
                        type="button"
                        :class="{ on: uy3Resim[idx].modo === 'Payment' }"
                        @click="uy3Resim[idx].modo = 'Payment'"
                      >
                        Valor da parcela
                      </button>
                    </div>
                    <div class="cc-input-area">
                      <input
                        :value="uy3Resim[idx].valor"
                        inputmode="numeric"
                        @input="uy3Resim[idx].valor = mascaraMoedaBR($event.target.value)"
                        placeholder="R$ 0,00"
                        class="tabular-nums"
                      />
                      <button
                        type="button"
                        :disabled="uy3Resim[idx].busy"
                        @click="resimUY3(idx)"
                      >
                        {{ uy3Resim[idx].busy ? '…' : 'Simular' }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- FACTA (v93: borda âmbar do banco) -->
            <div v-if="bancoAtivo('facta')" class="overflow-hidden rounded-xl border-2 border-n-amber-9/70">
              <div class="flex gap-2 items-center px-3 py-2 border-b border-n-amber-9/40 bg-n-amber-9">
                <span class="rounded-sm size-2.5 bg-black-900/70" />
                <b class="text-xs tracking-wider text-black-900">FACTA</b>
                <span class="ml-auto text-[10px] uppercase text-black-900/70">
                  {{ factaState?.ofertas?.length ? `${factaState.ofertas.length} oferta(s)` : factaState?.status || '—' }}
                </span>
              </div>
              <div class="flex flex-col gap-2 p-2.5">
                <p v-if="!factaState" class="py-4 text-xs text-center text-n-slate-11">
                  Busque as ofertas
                </p>
                <div v-else-if="factaState.status === 'em_andamento'" class="py-3 text-center">
                  <p class="text-xs text-n-slate-11">Consulta em andamento na FACTA…</p>
                  <button
                    type="button"
                    class="px-5 py-2 mt-2 text-xs font-bold text-black rounded-xl bg-n-amber-9 hover:opacity-90"
                    @click="buscarFACTA"
                  >
                    ↻ Atualizar
                  </button>
                </div>
                <div v-else-if="!factaState.ofertas.length" class="p-3 text-[11px] rounded-lg border border-dashed border-n-ruby-7 text-n-ruby-11">
                  <b class="block text-[10px] uppercase">Não Elegível</b>
                  <p class="mt-1">{{ factaState.motivo || 'Sem ofertas FACTA' }}</p>
                </div>
                <!-- v100: card no padrão novo do Kairo (credito-card) -->
                <!-- v107: v-show amarra o card ao vínculo selecionado
                     (of.matricula); v-show preserva o índice do factaResim -->
                <div
                  v-for="(of, idx) in factaState?.ofertas || []"
                  v-show="factaCardVisivel(of)"
                  :key="idx"
                  class="credito-card"
                  :class="{ melhor: isMelhor(Number(of.valor_liquido) || 0) }"
                >
                  <div class="cc-header">
                    <div>
                      <span class="cc-label">Valor liberado</span>
                      <h2 class="cc-valor tabular-nums">{{ brl(of.valor_liquido) }}</h2>
                    </div>
                    <div class="cc-badges">
                      <span v-if="isMelhor(Number(of.valor_liquido) || 0)" class="cc-badge sucesso">
                        ★ Melhor do cliente
                      </span>
                      <span v-if="of.tabela" class="cc-badge leilao">
                        {{ String(of.tabela || '').split(' ')[0] }}
                      </span>
                      <span v-if="of.matricula" class="cc-badge leilao">
                        Matr. {{ of.matricula }}
                      </span>
                    </div>
                  </div>
                  <div class="cc-info">
                    <div>
                      <span>Prazo</span>
                      <strong>{{ of.prazo }}x</strong>
                    </div>
                    <div>
                      <span>Parcela</span>
                      <strong class="tabular-nums">{{ brl(of.parcela) }}</strong>
                    </div>
                    <div>
                      <span>Produto</span>
                      <strong>{{ of.tabela || '—' }}<template v-if="of.taxa"> · {{ of.taxa }}% a.m.</template></strong>
                    </div>
                  </div>
                  <div class="cc-id">
                    ID da simulação: <b class="tabular-nums">{{ of._idSimulador || '—' }}</b>
                  </div>
                  <div class="cc-actions">
                    <button
                      type="button"
                      class="cc-btn"
                      @click="
                        copiarOferta({
                          valor: brl(of.valor_liquido),
                          prazo: of.prazo,
                          parcela: brl(of.parcela),
                        })
                      "
                    >
                      Copiar
                    </button>
                    <button
                      type="button"
                      class="cc-btn digitar"
                      :disabled="digBusy || (factaResim[idx] && factaResim[idx].busy)"
                      @click="abrirDigitarFACTA(idx)"
                    >
                      Digitar esta
                    </button>
                  </div>
                  <p v-if="factaResim[idx] && factaResim[idx].erro" class="cc-erro">
                    {{ factaResim[idx].erro }}
                  </p>
                  <div v-if="factaResim[idx]" class="cc-simbox">
                    <label>Simular por parcela</label>
                    <div class="cc-input-area">
                      <input
                        :value="factaResim[idx].parcela"
                        inputmode="numeric"
                        @input="factaResim[idx].parcela = mascaraMoedaBR($event.target.value)"
                        placeholder="R$ 0,00"
                        class="tabular-nums"
                      />
                      <button
                        type="button"
                        :disabled="factaResim[idx].busy"
                        @click="resimFACTA(idx)"
                      >
                        {{ factaResim[idx].busy ? '…' : 'Simular' }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- C6 (v93: VERDE com borda verde, pedido do Kairo) -->
            <div v-if="bancoAtivo('c6')" class="overflow-hidden rounded-xl border-2 border-n-teal-9/70">
              <div class="flex gap-2 items-center px-3 py-2 border-b border-n-teal-9/40 bg-n-teal-9">
                <span class="bg-white rounded-sm size-2.5" />
                <b class="text-xs tracking-wider text-white">C6</b>
                <span class="ml-auto text-[10px] uppercase text-white/80">
                  {{ c6Sim?.oferta ? 'OFERTA MÁXIMA' : '—' }}
                </span>
              </div>
              <div class="flex flex-col gap-2 p-2.5">
                <p v-if="c6Sim === null" class="py-4 text-xs text-center text-n-slate-11">
                  Busque as ofertas
                </p>
                <p v-else-if="c6Sim.semlink" class="p-3 text-[11px] rounded-lg border border-dashed border-n-teal-7 text-n-teal-11">
                  Cliente ainda não tem autorização C6 — use "Autorizar bancos"
                  para gerar o link de biometria.
                </p>
                <!-- v123: aguardando = só os botões; retorno da consulta
                     aparece AQUI dentro por 10s e some -->
                <div v-else-if="c6Sim.aguardando" class="flex flex-col gap-2 p-3 rounded-lg border border-dashed border-n-amber-7">
                  <p v-if="c6Msg" class="text-[11px] font-bold text-n-amber-11">
                    {{ c6Msg }}
                  </p>
                  <!-- v137: botões sólidos, centralizados e afastados -->
                  <div class="flex flex-wrap gap-6 justify-center py-1">
                    <button
                      type="button"
                      :disabled="busy === 'c6consulta'"
                      class="px-5 py-2 text-xs font-bold text-white rounded-xl bg-n-teal-9 hover:opacity-90"
                      @click="consultarAutorizacaoC6"
                    >
                      {{ busy === 'c6consulta' ? 'Consultando…' : 'Consultar autorização' }}
                    </button>
                    <button
                      v-if="c6Sim.link"
                      type="button"
                      class="px-5 py-2 text-xs font-bold text-white rounded-xl bg-n-teal-9 hover:opacity-90"
                      @click="copiar(c6Sim.link, 'Link C6')"
                    >
                      Copiar link
                    </button>
                  </div>
                </div>
                <div v-else-if="c6Sim.expirado" class="p-3 text-[11px] rounded-lg border border-dashed border-n-ruby-7 text-n-ruby-11">
                  Link EXPIRADO e o cliente não autorizou. Deseja gerar um novo?
                  <button
                    type="button"
                    :disabled="busy === 'c6novolink'"
                    class="block mt-2 px-2.5 py-1.5 text-[11px] font-bold rounded-lg border border-n-amber-9 text-n-amber-11 hover:bg-n-amber-3"
                    @click="gerarNovoLinkC6"
                  >
                    {{ busy === 'c6novolink' ? 'Gerando…' : 'Gerar novo link C6' }}
                  </button>
                </div>
                <p v-else-if="c6Sim.erro" class="py-4 text-xs text-center text-n-slate-11">
                  {{ c6Sim.erro }}
                </p>
                <!-- v122: OFERTA MÁXIMA (c6/oferta) + condições da simulação v2 -->
                <div
                  v-else-if="c6Sim.oferta"
                  class="credito-card"
                  :class="{ melhor: isMelhor(Number(c6Sim.oferta.valor_cliente) || 0) }"
                >
                  <div class="cc-header">
                    <div>
                      <span class="cc-label">Valor liberado</span>
                      <h2 class="cc-valor tabular-nums">{{ brl(c6Sim.oferta.valor_cliente) }}</h2>
                    </div>
                    <div class="cc-badges">
                      <span v-if="isMelhor(Number(c6Sim.oferta.valor_cliente) || 0)" class="cc-badge sucesso">
                        ★ Melhor do cliente
                      </span>
                      <span class="cc-badge maxima">Máxima</span>
                    </div>
                  </div>
                  <div class="cc-info">
                    <div>
                      <span>Prazo</span>
                      <strong>{{ c6Sim.oferta.quantidade_parcelas }}x</strong>
                    </div>
                    <div>
                      <span>Parcela</span>
                      <strong class="tabular-nums">{{ brl(c6Sim.oferta.valor_parcela) }}</strong>
                    </div>
                    <div>
                      <span>Seguro</span>
                      <strong class="tabular-nums">{{ brl(c6Sim.oferta.valor_seguro) }}</strong>
                    </div>
                  </div>
                  <div class="cc-actions">
                    <button
                      type="button"
                      class="cc-btn"
                      @click="
                        copiarOferta({
                          valor: brl(c6Sim.oferta.valor_cliente),
                          prazo: c6Sim.oferta.quantidade_parcelas,
                          parcela: brl(c6Sim.oferta.valor_parcela),
                        })
                      "
                    >
                      Copiar
                    </button>
                    <button
                      type="button"
                      class="cc-btn digitar"
                      :disabled="c6Resim.busy || digBusy"
                      @click="digitarDaOfertaC6"
                    >
                      {{ c6Resim.busy ? 'Gerando…' : 'Digitar' }}
                    </button>
                  </div>
                  <!-- v122: condições retornadas pela simulação — escolher e digitar -->
                  <div v-if="c6Sim.condicoes && c6Sim.condicoes.length" class="flex flex-col gap-1.5 mt-2">
                    <span class="text-[10px] font-bold uppercase text-n-slate-11">
                      Condições ({{ c6Sim.condicoes.length }}) — escolha uma para digitar
                    </span>
                    <div
                      v-for="(c, ci) in c6Sim.condicoes"
                      :key="c.id_simulacao || ci"
                      class="flex flex-wrap gap-x-3 gap-y-1 items-center p-2 rounded-lg border border-n-slate-6"
                    >
                      <div class="flex flex-col min-w-0">
                        <!-- v136: VALOR LIBERADO em destaque, depois prazo/parcela -->
                        <span class="text-[10px] uppercase text-n-slate-11">Valor liberado</span>
                        <b class="text-base leading-tight text-n-teal-11 tabular-nums">
                          {{ brl(numBR(c.valor_liquido) || numBR(c.valor_cliente)) }}
                        </b>
                        <span class="text-[11px] text-n-slate-12 tabular-nums">
                          Prazo <b>{{ c.quantidade_parcelas }}x</b> · Parcela
                          <b>{{ brl(numBR(c.valor_parcela)) }}</b>
                        </span>
                        <span class="text-[10px] text-n-slate-10 tabular-nums truncate">
                          taxa {{ c.taxa_cliente_mensal }}% · {{ c.convenio?.descricao || 'Condição' }}
                          · ID {{ c.id_simulacao }}
                        </span>
                      </div>
                      <button
                        type="button"
                        class="cc-btn digitar ml-auto"
                        :disabled="digBusy"
                        @click="abrirDigitarC6(c)"
                      >
                        Digitar esta
                      </button>
                    </div>
                  </div>
                  <div class="cc-simbox">
                    <label>Simular (gera as condições para digitar)</label>
                    <div class="cc-toggle">
                      <button
                        type="button"
                        :class="{ on: c6Resim.tipo === 'POR_VALOR_PARCELA' }"
                        @click="c6Resim.tipo = 'POR_VALOR_PARCELA'"
                      >
                        Parcela
                      </button>
                      <button
                        type="button"
                        :class="{ on: c6Resim.tipo === 'POR_VALOR_SOLICITADO' }"
                        @click="c6Resim.tipo = 'POR_VALOR_SOLICITADO'"
                      >
                        Valor
                      </button>
                    </div>
                    <!-- v136: prazo CLICÁVEL (6/12/18/24/36/48/60) -->
                    <div class="flex flex-wrap gap-1 my-1.5">
                      <button
                        v-for="pz in C6_PRAZOS"
                        :key="pz"
                        type="button"
                        class="px-2 py-1 text-[11px] font-bold rounded-lg border tabular-nums"
                        :class="String(c6Resim.prazo) === String(pz)
                          ? 'bg-n-teal-9 text-white border-n-teal-9'
                          : 'border-n-slate-7 text-n-slate-11 hover:bg-n-slate-3'"
                        @click="c6Resim.prazo = String(pz)"
                      >
                        {{ pz }}x
                      </button>
                    </div>
                    <div class="cc-input-area">
                      <input
                        :value="c6Resim.valor"
                        placeholder="R$ 0,00"
                        class="tabular-nums"
                        inputmode="numeric"
                        @input="c6Resim.valor = mascaraMoedaBR($event.target.value)"
                      />
                      <button
                        type="button"
                        :disabled="c6Resim.busy"
                        @click="resimC6"
                      >
                        {{ c6Resim.busy ? '…' : 'Simular' }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- SOMA (v125: modelo UY3, card ROXO) -->
            <div v-if="bancoAtivo('soma')" class="overflow-hidden rounded-xl border-2 border-n-iris-9/70">
              <div class="flex gap-2 items-center px-3 py-2 border-b border-n-iris-9/40 bg-n-iris-9">
                <span class="bg-white rounded-sm size-2.5" />
                <b class="text-xs tracking-wider text-white">SOMA</b>
                <span class="ml-auto text-[10px] uppercase text-white/80">
                  {{ somaSim?.sims?.length ? `${somaSim.sims.length} SIMULAÇÃO(ÕES)` : '—' }}
                </span>
              </div>
              <div class="flex flex-col gap-2 p-2.5">
                <p v-if="somaMsg" class="p-2 text-[11px] font-bold rounded-lg border border-dashed border-n-amber-7 text-n-amber-11">
                  {{ somaMsg }}
                </p>
                <p v-if="somaSim === null" class="py-4 text-xs text-center text-n-slate-11">
                  Busque as ofertas
                </p>
                <div v-else-if="somaSim.consultando" class="p-3 text-[11px] rounded-lg border border-dashed border-n-iris-7 text-n-iris-11">
                  <span class="inline-block mr-1 rounded-full animate-pulse size-2 bg-n-iris-9" />
                  CONSULTANDO NO SOMA — o card atualiza sozinho quando o
                  retorno chegar (fila de 1 consulta/segundo).
                  <div class="flex justify-center mt-2">
                    <button
                      type="button"
                      class="px-5 py-2 text-xs font-bold text-white rounded-xl bg-n-iris-9 hover:opacity-90"
                      @click="atualizarSOMA"
                    >
                      ↻ Atualizar
                    </button>
                  </div>
                </div>
                <p v-else-if="somaSim.aguardando" class="p-3 text-[11px] rounded-lg border border-dashed border-n-amber-7 text-n-amber-11">
                  AGUARDANDO MARGEM no SOMA — clique em "Buscar ofertas" de novo
                  em instantes.
                </p>
                <div v-else-if="somaSim.naoElegivel" class="p-3 text-[11px] rounded-lg border border-dashed border-n-ruby-7 text-n-ruby-11">
                  <b>NÃO ELEGÍVEL</b>
                  <p v-if="somaSim.mensagem" class="mt-1">{{ somaSim.mensagem }}</p>
                  <ul v-if="somaSim.motivos.length" class="mt-1 list-disc list-inside">
                    <li v-for="(m, mi) in somaSim.motivos" :key="mi">{{ m }}</li>
                  </ul>
                </div>
                <p v-else-if="somaSim.erro" class="py-4 text-xs text-center text-n-slate-11">
                  {{ somaSim.erro }}
                </p>
                <template v-else>
                  <div v-if="!somaSim.sims.length" class="p-3 text-[11px] rounded-lg border border-dashed border-n-amber-7 text-n-amber-11">
                    Consulta OK ({{ somaSim.con?.conStatusNome }}) — sem
                    simulações ainda. Use "Simular novo valor" abaixo.
                    <div class="flex gap-1.5 items-center mt-2">
                      <span class="text-[10px] font-bold uppercase">Bancarizadora</span>
                      <button
                        v-for="bb in ['CELCOIN', 'UY3']"
                        :key="bb"
                        type="button"
                        class="px-2 py-1 text-[10px] font-bold rounded-lg border"
                        :class="somaBanca === bb
                          ? 'bg-n-iris-9 text-white border-n-iris-9'
                          : 'border-n-slate-7 text-n-slate-11 hover:bg-n-slate-3'"
                        @click="trocarBancaSoma(bb)"
                      >
                        {{ bb }}
                      </button>
                    </div>
                  </div>
                  <div
                    v-for="(s, si) in somaSim.sims"
                    :key="s.simId || si"
                    class="credito-card"
                    :class="{ melhor: isMelhor(Number(s.simValorLiquido) || 0) }"
                  >
                    <div class="cc-header">
                      <div>
                        <span class="cc-label">Valor liberado</span>
                        <h2 class="cc-valor tabular-nums">{{ brl(numBR(s.simValorLiquido)) }}</h2>
                      </div>
                      <div class="cc-badges">
                        <span v-if="isMelhor(Number(s.simValorLiquido) || 0)" class="cc-badge sucesso">
                          ★ Melhor do cliente
                        </span>
                        <span class="cc-badge maxima">{{ s.simBancarizadora || somaBanca }}</span>
                      </div>
                    </div>
                    <div class="cc-info">
                      <div>
                        <span>Prazo</span>
                        <strong>{{ s.simPrazo }}x</strong>
                      </div>
                      <div>
                        <span>Parcela</span>
                        <strong class="tabular-nums">{{ brl(numBR(s.simValorParcela)) }}</strong>
                      </div>
                      <div>
                        <span>Taxa</span>
                        <strong class="tabular-nums">{{ s.simTaxaMensal }}%</strong>
                      </div>
                    </div>
                    <div class="cc-id">
                      ID da simulação: <b class="tabular-nums">{{ s.simId }}</b>
                    </div>
                    <!-- v130: bancarizadora no card — troca e re-consulta aqui -->
                    <div class="flex gap-1.5 items-center my-1.5">
                      <span class="text-[10px] font-bold uppercase text-n-slate-11">Bancarizadora</span>
                      <button
                        v-for="bb in ['CELCOIN', 'UY3']"
                        :key="bb"
                        type="button"
                        class="px-2 py-1 text-[10px] font-bold rounded-lg border"
                        :class="somaBanca === bb
                          ? 'bg-n-iris-9 text-white border-n-iris-9'
                          : 'border-n-slate-7 text-n-slate-11 hover:bg-n-slate-3'"
                        @click="trocarBancaSoma(bb)"
                      >
                        {{ bb }}
                      </button>
                    </div>
                    <div class="cc-actions">
                      <button
                        type="button"
                        class="cc-btn"
                        @click="
                          copiarOferta({
                            valor: brl(numBR(s.simValorLiquido)),
                            prazo: s.simPrazo,
                            parcela: brl(numBR(s.simValorParcela)),
                          })
                        "
                      >
                        Copiar
                      </button>
                      <button
                        type="button"
                        class="cc-btn digitar"
                        :disabled="digBusy"
                        @click="abrirDigitarSOMA(s)"
                      >
                        Digitar esta
                      </button>
                    </div>
                    <!-- v142: re-sim POR CARD — cada simulação tem seu próprio
                         Simular novo valor e atualiza SÓ este card -->
                    <div v-if="somaResim[si]" class="cc-simbox">
                      <label>Simular novo valor</label>
                      <div class="cc-toggle">
                        <button
                          type="button"
                          :class="{ on: somaResim[si].tipo === 'valor-liberado' }"
                          @click="somaResim[si].tipo = 'valor-liberado'"
                        >
                          Valor líquido
                        </button>
                        <button
                          type="button"
                          :class="{ on: somaResim[si].tipo === 'valor-parcela' }"
                          @click="somaResim[si].tipo = 'valor-parcela'"
                        >
                          Valor da parcela
                        </button>
                      </div>
                      <!-- v128: prazo CLICÁVEL (6/8/12/18/24/36/48/60) -->
                      <div class="flex flex-wrap gap-1 my-1.5">
                        <button
                          v-for="pz in SOMA_PRAZOS"
                          :key="pz"
                          type="button"
                          class="px-2 py-1 text-[11px] font-bold rounded-lg border tabular-nums"
                          :class="String(somaResim[si].prazo) === String(pz)
                            ? 'bg-n-teal-9 text-white border-n-teal-9'
                            : 'border-n-slate-7 text-n-slate-11 hover:bg-n-slate-3'"
                          @click="somaResim[si].prazo = String(pz)"
                        >
                          {{ pz }}x
                        </button>
                      </div>
                      <div class="cc-input-area">
                        <input
                          :value="somaResim[si].valor"
                          placeholder="R$ 0,00"
                          class="tabular-nums"
                          inputmode="numeric"
                          @input="somaResim[si].valor = mascaraMoedaBR($event.target.value)"
                        />
                        <button
                          type="button"
                          :disabled="somaResim[si].busy"
                          @click="resimSOMA(si)"
                        >
                          {{ somaResim[si].busy ? '…' : 'Simular' }}
                        </button>
                      </div>
                    </div>
                  </div>
                  <!-- v142: simbox avulso SÓ quando ainda não há simulações -->
                  <div v-if="!somaSim.sims.length && somaResim.vazio" class="cc-simbox">
                    <label>Simular novo valor</label>
                    <div class="cc-toggle">
                      <button
                        type="button"
                        :class="{ on: somaResim.vazio.tipo === 'valor-liberado' }"
                        @click="somaResim.vazio.tipo = 'valor-liberado'"
                      >
                        Valor líquido
                      </button>
                      <button
                        type="button"
                        :class="{ on: somaResim.vazio.tipo === 'valor-parcela' }"
                        @click="somaResim.vazio.tipo = 'valor-parcela'"
                      >
                        Valor da parcela
                      </button>
                    </div>
                    <div class="flex flex-wrap gap-1 my-1.5">
                      <button
                        v-for="pz in SOMA_PRAZOS"
                        :key="pz"
                        type="button"
                        class="px-2 py-1 text-[11px] font-bold rounded-lg border tabular-nums"
                        :class="String(somaResim.vazio.prazo) === String(pz)
                          ? 'bg-n-teal-9 text-white border-n-teal-9'
                          : 'border-n-slate-7 text-n-slate-11 hover:bg-n-slate-3'"
                        @click="somaResim.vazio.prazo = String(pz)"
                      >
                        {{ pz }}x
                      </button>
                    </div>
                    <div class="cc-input-area">
                      <input
                        :value="somaResim.vazio.valor"
                        placeholder="R$ 0,00"
                        class="tabular-nums"
                        inputmode="numeric"
                        @input="somaResim.vazio.valor = mascaraMoedaBR($event.target.value)"
                      />
                      <button
                        type="button"
                        :disabled="somaResim.vazio.busy"
                        @click="resimSOMA('vazio')"
                      >
                        {{ somaResim.vazio.busy ? '…' : 'Simular' }}
                      </button>
                    </div>
                  </div>
                </template>
              </div>
            </div>

            <!-- V8 (Fase 2) — card verde -->
            <div
              v-if="bancoAtivo('v8')"
              class="overflow-hidden rounded-xl border-2 border-n-ruby-9/70"
            >
              <div
                class="flex gap-2 items-center px-3 py-2 border-b bg-n-ruby-9 border-n-ruby-9/40"
              >
                <span class="bg-white rounded-sm size-2.5" />
                <b class="text-xs tracking-wider text-white">V8</b>
                <span class="ml-auto text-[10px] uppercase text-white/80">
                  {{ v8Estado && v8Estado.status === 'SUCCESS' ? brl(v8Estado.margem) : (v8Estado && v8Estado.status || '—') }}
                </span>
              </div>
              <div class="flex flex-col gap-2 p-2.5">
                <!-- inicial: ainda não consultou -->
                <p v-if="!v8Consult && !v8Consultando" class="py-4 text-xs text-center text-n-slate-11">
                  Clique em "Autorizar bancos".
                </p>
                <!-- CONSULTANDO / rate-limit: mensagem + botão (padrão ↻ do SOMA);
                     some quando vem retorno -->
                <div
                  v-else-if="v8Consultando"
                  class="p-3 text-[11px] rounded-lg border border-dashed border-n-weak text-n-slate-11"
                >
                  <span class="inline-block mr-1 rounded-full animate-pulse size-2 bg-n-ruby-9" />
                  {{ v8Cooldown > 0 ? `Limite da V8 — aguarde ${v8Cooldown}s` : 'Consultando margem no V8 — o card atualiza sozinho.' }}
                  <div class="flex justify-center mt-2">
                    <button
                      type="button"
                      :disabled="v8Busy || v8Cooldown > 0"
                      class="px-5 py-2 text-xs font-bold text-white rounded-xl bg-n-ruby-9 disabled:opacity-50"
                      @click="atualizarV8"
                    >
                      {{ v8Cooldown > 0 ? `↻ Aguarde ${v8Cooldown}s` : '↻ Atualizar' }}
                    </button>
                  </div>
                </div>
                <!-- terminal SEM sucesso (REJECTED/CONSENT_APPROVED/ERROR): SÓ mensagem, sem botão -->
                <div
                  v-else-if="v8Estado && v8Estado.status && v8Estado.status !== 'SUCCESS'"
                  class="p-3 text-[11px] rounded-lg border border-dashed border-n-ruby-7 text-n-ruby-11"
                >
                  <b class="block text-[10px] uppercase">{{ v8Estado.status }}</b>
                  <p class="mt-1">{{ v8Estado.erro || 'V8 não liberou margem para este CPF.' }}</p>
                </div>
                <template v-else-if="v8Estado && v8Estado.status === 'SUCCESS'">
                  <div class="text-[11px] text-n-slate-11 mb-0.5">
                    Margem: <b class="text-n-slate-12">{{ brl(v8Estado.margem) }}</b>
                    <span v-if="v8Estado.employerName"> · {{ v8Estado.employerName }}</span>
                  </div>

                  <!-- card no MESMO layout do SOMA (credito-card) -->
                  <div class="credito-card" :class="{ melhor: v8Melhor }">
                    <div class="cc-header">
                      <div>
                        <span class="cc-label">Valor liberado</span>
                        <h2 class="cc-valor tabular-nums">{{ brl(v8CardLiquido) }}</h2>
                      </div>
                      <div class="cc-badges">
                        <span v-if="v8Melhor" class="cc-badge sucesso">★ Melhor do cliente</span>
                        <span v-if="v8Sim && v8Sim.provider" class="cc-badge maxima">{{ v8Sim.provider }}</span>
                        <span class="cc-badge maxima">{{ v8Sim ? 'SIMULADO' : 'MÁXIMO' }}</span>
                      </div>
                    </div>
                    <div class="cc-info">
                      <div>
                        <span>Prazo</span>
                        <strong>{{ v8CardPrazo }}x</strong>
                      </div>
                      <div>
                        <span>Parcela</span>
                        <strong class="tabular-nums">{{ brl(v8CardParcela) }}</strong>
                      </div>
                      <div>
                        <span>Taxa</span>
                        <strong class="tabular-nums">{{ v8CardTaxa }}</strong>
                      </div>
                    </div>
                    <div v-if="v8Sim" class="cc-id">
                      ID da simulação: <b class="tabular-nums">{{ v8Sim.id_simulation }}</b>
                    </div>
                    <p v-else-if="v8SimErro" class="p-2 text-[11px] rounded-lg border border-dashed border-n-ruby-7 text-n-ruby-11">
                      {{ v8SimErro }}<template v-if="erroEhDeValor">
                        — digite um valor menor e clique Simular.</template>
                    </p>

                    <!-- prazos disponíveis da V8: clicar ALTERA o valor do card -->
                    <div class="flex flex-wrap gap-1 my-1.5">
                      <button
                        v-for="o in (v8Offers || [])"
                        :key="o.installmentNumbers"
                        type="button"
                        class="px-2 py-1 text-[11px] font-bold rounded-lg border tabular-nums"
                        :class="String(v8SelPrazo) === String(o.installmentNumbers)
                          ? 'bg-n-ruby-9 text-white border-n-ruby-9'
                          : 'border-n-slate-7 text-n-slate-11 hover:bg-n-slate-3'"
                        @click="selecionarPrazoV8(o.installmentNumbers)"
                      >
                        {{ o.installmentNumbers }}x
                      </button>
                    </div>

                    <div class="cc-actions">
                      <button
                        type="button"
                        class="cc-btn"
                        @click="copiarOferta({ valor: brl(v8CardLiquido), prazo: v8CardPrazo, parcela: brl(v8CardParcela) })"
                      >
                        Copiar
                      </button>
                      <button
                        type="button"
                        class="cc-btn digitar"
                        :disabled="!v8Sim || v8Form.busy"
                        @click="abrirDigitarV8"
                      >
                        Digitar esta
                      </button>
                    </div>

                    <!-- Simular novo valor (igual SOMA): por líquido ou parcela -->
                    <div class="cc-simbox">
                      <label>Simular novo valor</label>
                      <div class="cc-toggle">
                        <button type="button" :class="{ on: v8Form.modo === 'valor_liquido' }" @click="v8Form.modo = 'valor_liquido'">
                          Valor líquido
                        </button>
                        <button type="button" :class="{ on: v8Form.modo === 'parcela' }" @click="v8Form.modo = 'parcela'">
                          Valor da parcela
                        </button>
                      </div>
                      <label class="flex gap-2 items-center text-[11px] cursor-pointer my-1 text-n-slate-11">
                        <input v-model="v8Form.comSeguro" type="checkbox" @change="carregarV8Offers()"> Com seguro
                      </label>
                      <div class="cc-input-area">
                        <input
                          :value="v8Form.valor"
                          placeholder="R$ 0,00 (vazio = máximo)"
                          class="tabular-nums"
                          inputmode="numeric"
                          @input="v8Form.valor = mascaraMoedaBR($event.target.value)"
                        />
                        <button type="button" :disabled="v8Form.busy" @click="simularV8">
                          {{ v8Form.busy ? '…' : 'Simular' }}
                        </button>
                      </div>
                    </div>

                    <a
                      v-if="v8Prop && v8Prop.formalization_url"
                      :href="v8Prop.formalization_url"
                      target="_blank"
                      class="block mt-2 text-[11px] text-n-ruby-11 underline break-all"
                    >
                      Link de formalização →
                    </a>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </template>

        <!-- ================= VIEW: PROPOSTAS (v94) ================= -->
        <template v-else-if="view === 'propostas'">
          <div class="flex flex-wrap gap-2 items-center mb-3">
            <span class="inline-flex gap-1.5 items-center px-2.5 py-1.5 text-[11px] font-bold text-white rounded-lg bg-n-teal-9">
              <span class="inline-block bg-white rounded-full animate-pulse size-2" />
              AO VIVO
            </span>
            <span class="text-xs text-n-slate-11">Propostas do CPF <b class="tabular-nums text-n-slate-12">{{ cpf }}</b></span>
            <span class="flex-1" />
            <button
              type="button"
              class="px-3 py-1.5 text-xs font-bold text-white rounded-lg bg-n-blue-9 hover:bg-n-blue-10 disabled:opacity-50"
              :disabled="propostasBusy"
              @click="carregarPropostas"
            >
              {{ propostasBusy ? 'Atualizando…' : '↻ Atualizar' }}
            </button>
            <button
              type="button"
              class="px-3 py-1.5 text-xs font-bold text-white bg-red-500 rounded-lg hover:bg-red-600"
              @click="fecharPropostas"
            >
              ✕ Fechar
            </button>
          </div>

          <p v-if="propostas === null" class="py-8 text-sm text-center text-n-slate-11">
            Consultando os bancos em paralelo…
          </p>

          <div v-else class="flex flex-col gap-3">
            <!-- UY3 -->
            <div v-if="bancoAtivo('uy3')" class="overflow-hidden rounded-xl border-2 border-n-blue-9/70">
              <div class="flex gap-2 items-center px-3 py-2 bg-n-blue-9">
                <b class="text-xs tracking-wider text-white">UY3</b>
                <span class="ml-auto text-[10px] uppercase text-white/80">
                  {{ bancoErro('uy3') ? 'ERRO' : `${uy3Items.length} proposta(s)` }}
                </span>
              </div>
              <div class="flex flex-col gap-2 p-2.5">
                <p v-if="bancoErro('uy3')" class="text-xs text-n-ruby-11">{{ bancoErro('uy3') }}</p>
                <p v-else-if="!uy3Items.length" class="py-3 text-xs text-center text-n-slate-11">
                  Nenhuma proposta UY3 para este CPF.
                </p>
                <div v-for="p in uy3Items" :key="p.id" class="p-3 rounded-lg border-2 border-n-blue-7 bg-n-solid-1">
                  <div class="flex flex-wrap gap-2 items-center">
                    <b class="text-sm text-n-slate-12">{{ p.__person_name || `Proposta ${p.creditNoteNo || ''}` }}</b>
                    <span class="px-2 py-0.5 text-[10px] font-bold rounded" :class="STATUS_BADGE[statusCls(p.status)]">
                      {{ (p.__resumo?.statusDisplay || p.statusDisplay || p.status || '').toUpperCase() }}
                    </span>
                    <span class="flex-1" />
                    <span class="text-[11px] text-n-slate-11">{{ fmtData(p.createdAt || p.acceptanceDate) }}</span>
                  </div>
                  <p class="mt-1 text-[12px] text-n-slate-12">
                    Nº <b class="tabular-nums">{{ p.creditNoteNo || p.id }}</b>
                    <template v-if="p.__resumo?.valorLiquido != null"> · Liberado <b>{{ cents(p.__resumo.valorLiquido) }}</b></template>
                    <template v-if="p.__resumo?.prazo"> · <b>{{ p.__resumo.prazo }}x</b> de <b>{{ cents(p.__resumo.parcela) }}</b></template>
                    <template v-if="p.__resumo"> · Assinatura: <b>{{ Number(p.__resumo.unicoSignScore) > 0 ? 'ASSINADO' : 'não assinou' }}</b></template>
                  </p>
                  <p class="mt-1 text-[12px] text-n-slate-12">
                    Data da Digitação <b>{{ fmtData(p.__resumo?.dataDigitacao || p.createdAt) || '—' }}</b>
                    · Data de Pagamento <b>{{ fmtData(p.__resumo?.dataPagamento) || '—' }}</b>
                    · CPF <b class="tabular-nums">{{ p.personRegistrationNumber || '—' }}</b>
                  </p>
                  <p class="mt-0.5 text-[11px] text-n-slate-11">
                    UUID <span class="tabular-nums text-n-slate-12">{{ p.id }}</span>
                    · Tabela Digitada <span class="text-n-slate-12">{{ p.__resumo?.produto || p.productIdDisplay || '—' }}</span>
                  </p>
                  <p v-if="p.__resumo?.faseAtual" class="mt-0.5 text-[11px] text-n-slate-11">
                    Fase: {{ p.__resumo.faseAtual.nome }}<template v-if="p.__resumo.faseAtual.descricao"> — {{ p.__resumo.faseAtual.descricao }}</template>
                  </p>
                  <div class="flex flex-wrap gap-1.5 items-center mt-2">
                    <button
                      v-if="extraiUrl(p.__signUrl)"
                      type="button"
                      class="px-3 py-1 text-xs font-bold text-white rounded-lg bg-n-teal-9 hover:opacity-90"
                      @click="copiar(extraiUrl(p.__signUrl), 'Link de assinatura')"
                    >
                      Copiar link
                    </button>
                    <button
                      v-else-if="!uy3Finalizada(p)"
                      type="button"
                      class="px-3 py-1 text-xs font-semibold rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
                      @click="buscarLinkUY3(p)"
                    >
                      ↻ Buscar link
                    </button>
                    <button
                      v-if="isRevisaoPix(p.__resumo?.statusDisplay || p.statusDisplay || p.status)"
                      type="button"
                      class="px-3 py-1 text-xs font-bold text-black-900 bg-yellow-400 rounded-lg hover:bg-yellow-500"
                      @click="abrirReapr(p)"
                    >
                      ⚠️ Reapresentar PIX
                    </button>
                    <span class="flex-1" />
                    <button
                      v-if="!uy3Finalizada(p)"
                      type="button"
                      class="px-3 py-1 text-xs font-bold rounded-lg border text-n-ruby-11 border-n-ruby-9 hover:bg-n-ruby-3"
                      @click="cancelarUY3(p)"
                    >
                      Cancelar
                    </button>
                  </div>
                  <!-- reapresentar PIX inline -->
                  <div
                    v-if="reapr[p.id] && reapr[p.id].open"
                    class="flex flex-wrap gap-1.5 items-center p-2 mt-2 rounded-lg border border-dashed border-n-amber-9"
                  >
                    <span class="text-[11px] text-n-slate-11">
                      Chave atual: <b class="text-n-slate-12">{{ p.__resumo?.pixTipoAtual || '—' }} {{ p.__resumo?.pixChaveAtual || '' }}</b>
                    </span>
                    <select
                      v-model="reapr[p.id].tipo"
                      class="px-1.5 py-1 text-[11px] rounded border border-n-weak bg-n-background text-n-slate-12"
                      @change="ajustaChaveReapr(p.id)"
                    >
                      <option value="NaturalRegistrationNumber">CPF</option>
                      <option value="Phone">Telefone</option>
                      <option value="Email">E-mail</option>
                      <option value="Automatic">Aleatória</option>
                    </select>
                    <input
                      v-model="reapr[p.id].chave"
                      placeholder="Chave PIX"
                      class="px-1.5 py-1 w-44 text-[11px] rounded border border-n-weak bg-n-background text-n-slate-12"
                      :disabled="reapr[p.id].tipo === 'NaturalRegistrationNumber'"
                    />
                    <button
                      type="button"
                      class="px-2.5 py-1 text-[11px] font-bold text-black-900 bg-yellow-400 rounded-lg hover:bg-yellow-500 disabled:opacity-50"
                      :disabled="reapr[p.id].busy"
                      @click="submitReapr(p.id)"
                    >
                      {{ reapr[p.id].busy ? '…' : 'Reapresentar' }}
                    </button>
                    <button
                      type="button"
                      class="px-2 py-1 text-[11px] rounded-lg border border-n-weak text-n-slate-11"
                      @click="reapr[p.id].open = false"
                    >
                      fechar
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- FACTA -->
            <div v-if="bancoAtivo('facta')" class="overflow-hidden rounded-xl border-2 border-n-amber-9/70">
              <div class="flex gap-2 items-center px-3 py-2 bg-n-amber-9">
                <b class="text-xs tracking-wider text-black-900">FACTA</b>
                <span class="ml-auto text-[10px] uppercase text-black-900/70">
                  {{ bancoErro('facta') ? 'ERRO' : `${factaItems.length} proposta(s)` }}
                </span>
              </div>
              <div class="flex flex-col gap-2 p-2.5">
                <p v-if="bancoErro('facta')" class="text-xs text-n-ruby-11">{{ bancoErro('facta') }}</p>
                <p v-else-if="!factaItems.length" class="py-3 text-xs text-center text-n-slate-11">
                  {{ propostas.facta?.mensagem || 'Nenhuma proposta FACTA para este CPF.' }}
                </p>
                <div v-for="(p, i) in factaItems" :key="i" class="p-3 rounded-lg border-2 border-n-amber-7 bg-n-solid-1">
                  <div class="flex flex-wrap gap-2 items-center">
                    <b class="text-sm tabular-nums text-n-slate-12">Nº {{ p.codigo || p.proposta || '—' }}</b>
                    <span class="px-2 py-0.5 text-[10px] font-bold rounded" :class="STATUS_BADGE[statusCls(p.situacao || p.status)]">
                      {{ (p.situacao || p.status || '').toUpperCase() }}
                    </span>
                    <span class="flex-1" />
                    <span class="text-[11px] text-n-slate-11">{{ p.data_emissao || p.data_ccb || '' }}</span>
                  </div>
                  <p class="mt-1 text-[12px] text-n-slate-12">
                    Valor <b>{{ brl(p.valor_ccb || p.valor) }}</b>
                  </p>
                  <div v-if="p.url_assinatura" class="flex gap-1.5 items-center mt-2">
                    <button
                      type="button"
                      class="px-3 py-1 text-xs font-bold text-white rounded-lg bg-n-teal-9 hover:opacity-90"
                      @click="copiar(p.url_assinatura, 'Link FACTA')"
                    >
                      Copiar link
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- C6 -->
            <div v-if="bancoAtivo('c6')" class="overflow-hidden rounded-xl border-2 border-n-teal-9/70">
              <div class="flex gap-2 items-center px-3 py-2 bg-n-teal-9">
                <b class="text-xs tracking-wider text-white">C6</b>
                <span class="ml-auto text-[10px] uppercase text-white/80">
                  {{ bancoErro('c6') ? 'ERRO' : `${c6Items.length} proposta(s)` }}
                </span>
              </div>
              <div class="flex flex-col gap-2 p-2.5">
                <p v-if="bancoErro('c6')" class="text-xs text-n-ruby-11">{{ bancoErro('c6') }}</p>
                <p v-else-if="!c6Items.length" class="py-3 text-xs text-center text-n-slate-11">
                  Nenhuma proposta C6 para este CPF.
                </p>
                <!-- v138: card GRANDE — valor/prazo/parcela em blocos, digitador e link visível -->
                <div v-for="r in c6Items" :key="r.proposta_id" class="p-4 rounded-xl border-2 border-n-teal-7 bg-n-solid-1">
                  <div class="flex flex-wrap gap-2 items-center">
                    <b class="text-base tabular-nums text-n-slate-12">Nº {{ r.proposta_id }}</b>
                    <span
                      class="px-2 py-0.5 text-[10px] font-bold rounded"
                      :class="STATUS_BADGE[statusCls(r.detalhe?.status || r.detalhe?.situacao)]"
                    >
                      {{ (r.detalhe?.status || r.detalhe?.situacao || '—').toUpperCase() }}
                    </span>
                    <span class="flex-1" />
                    <span class="text-[11px] text-n-slate-11">{{ fmtData(r.criado_em) }}</span>
                  </div>
                  <p v-if="r.digitado_por" class="mt-0.5 text-[11px] text-n-slate-11">
                    Digitado por <b class="text-n-slate-12">{{ r.digitado_por }}</b>
                  </p>
                  <div class="grid grid-cols-3 gap-2 mt-3">
                    <div class="p-2.5 text-center rounded-lg bg-n-alpha-2">
                      <span class="block text-[10px] uppercase text-n-slate-11">Valor liberado</span>
                      <b class="text-lg leading-tight text-n-teal-11 tabular-nums">
                        {{ brl(numBR(r.valor_liquido) || numBR(r.detalhe?.valor || r.detalhe?.valor_total)) }}
                      </b>
                    </div>
                    <div class="p-2.5 text-center rounded-lg bg-n-alpha-2">
                      <span class="block text-[10px] uppercase text-n-slate-11">Prazo</span>
                      <b class="text-lg leading-tight text-n-slate-12 tabular-nums">{{ r.prazo ? `${r.prazo}x` : '—' }}</b>
                    </div>
                    <div class="p-2.5 text-center rounded-lg bg-n-alpha-2">
                      <span class="block text-[10px] uppercase text-n-slate-11">Parcela</span>
                      <b class="text-lg leading-tight text-n-slate-12 tabular-nums">
                        {{ r.valor_parcela ? brl(numBR(r.valor_parcela)) : '—' }}
                      </b>
                    </div>
                  </div>
                  <p v-if="r.error" class="mt-2 text-[11px] text-n-ruby-11">{{ r.error }}</p>
                  <div v-if="extraiUrl(r.link)" class="mt-3">
                    <span class="block text-[10px] uppercase text-n-slate-11">Link de formalização</span>
                    <p class="p-2 mt-1 text-[11px] break-all rounded-lg bg-n-alpha-2 text-n-teal-11 tabular-nums">
                      {{ extraiUrl(r.link) }}
                    </p>
                    <div class="flex justify-center mt-2">
                      <button
                        type="button"
                        class="px-5 py-2 text-xs font-bold text-white rounded-xl bg-n-teal-9 hover:opacity-90"
                        @click="copiar(extraiUrl(r.link), 'Link C6 (formalização)')"
                      >
                        Copiar link
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- SOMA (v143) — histórico bank_proposal_soma + detalhe AO VIVO -->
            <div v-if="bancoAtivo('soma')" class="overflow-hidden rounded-xl border-2 border-n-iris-9/70">
              <div class="flex gap-2 items-center px-3 py-2 bg-n-iris-9">
                <b class="text-xs tracking-wider text-white">SOMA</b>
                <span class="ml-auto text-[10px] uppercase text-white/80">
                  {{ bancoErro('soma') ? 'ERRO' : `${somaItems.length} proposta(s)` }}
                </span>
              </div>
              <div class="flex flex-col gap-2 p-2.5">
                <p v-if="bancoErro('soma')" class="text-xs text-n-ruby-11">{{ bancoErro('soma') }}</p>
                <p v-else-if="!somaItems.length" class="py-3 text-xs text-center text-n-slate-11">
                  Nenhuma proposta SOMA para este CPF.
                </p>
                <div v-for="r in somaItems" :key="r.proposta_id" class="p-4 rounded-xl border-2 border-n-iris-7 bg-n-solid-1">
                  <div class="flex flex-wrap gap-2 items-center">
                    <b class="text-base tabular-nums text-n-slate-12">Nº {{ r.proposta_id }}</b>
                    <span
                      class="px-2 py-0.5 text-[10px] font-bold rounded"
                      :class="STATUS_BADGE[statusCls(r.detalhe?.proStatusNome)]"
                    >
                      {{ (r.detalhe?.proStatusNome || '—').toUpperCase() }}
                    </span>
                    <span v-if="r.bancarizadora" class="px-2 py-0.5 text-[10px] font-bold rounded bg-n-iris-3 text-n-iris-11">
                      {{ r.bancarizadora }}
                    </span>
                    <span class="flex-1" />
                    <span class="text-[11px] text-n-slate-11">{{ fmtData(r.criado_em) }}</span>
                  </div>
                  <p v-if="r.digitado_por" class="mt-0.5 text-[11px] text-n-slate-11">
                    Digitado por <b class="text-n-slate-12">{{ r.digitado_por }}</b>
                  </p>
                  <div class="grid grid-cols-3 gap-2 mt-3">
                    <div class="p-2.5 text-center rounded-lg bg-n-alpha-2">
                      <span class="block text-[10px] uppercase text-n-slate-11">Valor liberado</span>
                      <b class="text-lg leading-tight text-n-iris-11 tabular-nums">
                        {{ r.detalhe?.proValorLiquido != null ? brl(numBR(r.detalhe.proValorLiquido)) : '—' }}
                      </b>
                    </div>
                    <div class="p-2.5 text-center rounded-lg bg-n-alpha-2">
                      <span class="block text-[10px] uppercase text-n-slate-11">Prazo</span>
                      <b class="text-lg leading-tight text-n-slate-12 tabular-nums">
                        {{ r.detalhe?.proPeriodo ? `${r.detalhe.proPeriodo}x` : '—' }}
                      </b>
                    </div>
                    <div class="p-2.5 text-center rounded-lg bg-n-alpha-2">
                      <span class="block text-[10px] uppercase text-n-slate-11">Valor total</span>
                      <b class="text-lg leading-tight text-n-slate-12 tabular-nums">
                        {{ r.detalhe?.proValorTotal != null ? brl(numBR(r.detalhe.proValorTotal)) : '—' }}
                      </b>
                    </div>
                  </div>
                  <p v-if="r.detalhe?.proMotivoCancelamento" class="mt-2 text-[11px] text-n-ruby-11">
                    Motivo do cancelamento: {{ r.detalhe.proMotivoCancelamento }}
                  </p>
                  <p v-if="r.error" class="mt-2 text-[11px] text-n-ruby-11">{{ r.error }}</p>
                  <div v-if="extraiUrl(r.detalhe?.proLinkAssinatura)" class="mt-3">
                    <span class="block text-[10px] uppercase text-n-slate-11">Link de assinatura</span>
                    <p class="p-2 mt-1 text-[11px] break-all rounded-lg bg-n-alpha-2 text-n-iris-11 tabular-nums">
                      {{ extraiUrl(r.detalhe?.proLinkAssinatura) }}
                    </p>
                    <div class="flex justify-center mt-2">
                      <button
                        type="button"
                        class="px-5 py-2 text-xs font-bold text-white rounded-xl bg-n-iris-9 hover:opacity-90"
                        @click="copiar(extraiUrl(r.detalhe?.proLinkAssinatura), 'Link SOMA (assinatura)')"
                      >
                        Copiar link
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- V8 — histórico bank_proposal_v8 + status AO VIVO -->
            <div v-if="bancoAtivo('v8')" class="overflow-hidden rounded-xl border-2 border-n-ruby-9/70">
              <div class="flex gap-2 items-center px-3 py-2 bg-n-ruby-9">
                <b class="text-xs tracking-wider text-white">V8</b>
                <span class="ml-auto text-[10px] uppercase text-white/80">
                  {{ bancoErro('v8') ? 'ERRO' : `${v8Items.length} proposta(s)` }}
                </span>
              </div>
              <div class="flex flex-col gap-2 p-2.5">
                <p v-if="bancoErro('v8')" class="text-xs text-n-ruby-11">{{ bancoErro('v8') }}</p>
                <p v-else-if="!v8Items.length" class="py-3 text-xs text-center text-n-slate-11">
                  Nenhuma proposta V8 para este CPF.
                </p>
                <div v-for="r in v8Items" :key="r.proposta_id" class="p-4 rounded-xl border-2 border-n-ruby-7 bg-n-solid-1">
                  <div class="flex flex-wrap gap-2 items-center">
                    <b class="text-sm tabular-nums text-n-slate-12">Nº {{ r.proposta_id }}</b>
                    <span v-if="r.detalhe?.status" class="px-2 py-0.5 text-[10px] font-bold rounded bg-n-ruby-3 text-n-ruby-11">
                      {{ String(r.detalhe.status).toUpperCase() }}
                    </span>
                    <span class="flex-1" />
                    <span class="text-[11px] text-n-slate-11">{{ fmtData(r.criado_em) }}</span>
                  </div>
                  <p v-if="r.digitado_por" class="mt-0.5 text-[11px] text-n-slate-11">
                    Digitado por <b class="text-n-slate-12">{{ r.digitado_por }}</b>
                  </p>
                  <div class="grid grid-cols-3 gap-2 mt-3">
                    <div class="p-2.5 text-center rounded-lg bg-n-alpha-2">
                      <span class="block text-[10px] uppercase text-n-slate-11">Valor liberado</span>
                      <b class="text-lg leading-tight text-n-ruby-11 tabular-nums">
                        {{ r.valor_liberado != null ? brl(Number(r.valor_liberado)) : '—' }}
                      </b>
                    </div>
                    <div class="p-2.5 text-center rounded-lg bg-n-alpha-2">
                      <span class="block text-[10px] uppercase text-n-slate-11">Prazo</span>
                      <b class="text-lg leading-tight text-n-slate-12 tabular-nums">
                        {{ r.prazo ? `${r.prazo}x` : '—' }}
                      </b>
                    </div>
                    <div class="p-2.5 text-center rounded-lg bg-n-alpha-2">
                      <span class="block text-[10px] uppercase text-n-slate-11">Parcela</span>
                      <b class="text-lg leading-tight text-n-slate-12 tabular-nums">
                        {{ r.parcela != null ? brl(Number(r.parcela)) : '—' }}
                      </b>
                    </div>
                  </div>
                  <p v-if="r.error" class="mt-2 text-[11px] text-n-ruby-11">{{ r.error }}</p>
                  <div v-if="r.formalization_url" class="mt-3">
                    <span class="block text-[10px] uppercase text-n-slate-11">Link de formalização</span>
                    <p class="p-2 mt-1 text-[11px] break-all rounded-lg bg-n-alpha-2 text-n-ruby-11 tabular-nums">
                      {{ r.formalization_url }}
                    </p>
                    <div class="flex justify-center mt-2">
                      <button
                        type="button"
                        class="px-5 py-2 text-xs font-bold text-white rounded-xl bg-n-ruby-9 hover:opacity-90"
                        @click="copiar(r.formalization_url, 'Link V8 (formalização)')"
                      >
                        Copiar link
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- ================= VIEW: DIGITAR ================= -->
        <template v-else-if="view === 'digitar' && dig">
          <div class="grid grid-cols-1 gap-3 lg:grid-cols-[320px_1fr]">
            <!-- resumo (v94: borda na cor do banco) -->
            <div
              class="p-4 h-fit rounded-xl border-2 bg-n-solid-1"
              :class="{
                'border-n-blue-9/70': dig.banco === 'UY3',
                'border-n-amber-9/70': dig.banco === 'FACTA',
                'border-n-teal-9/70': dig.banco === 'C6',
                'border-n-iris-9/70': dig.banco === 'SOMA',
                'border-n-ruby-9/70': dig.banco === 'V8',
              }"
            >
              <span
                class="px-2 py-0.5 text-xs font-bold rounded"
                :class="{
                  'bg-n-blue-3 text-n-blue-11': dig.banco === 'UY3',
                  'bg-n-amber-3 text-n-amber-11': dig.banco === 'FACTA',
                  'bg-n-teal-3 text-n-teal-11': dig.banco === 'C6',
                  'bg-n-iris-3 text-n-iris-11': dig.banco === 'SOMA',
                  'bg-n-ruby-3 text-n-ruby-11': dig.banco === 'V8',
                }"
              >
                {{ dig.banco }}
              </span>
              <p class="mt-3 text-[10px] uppercase tracking-wide text-n-slate-11">
                Valor liberado
              </p>
              <p class="text-2xl font-bold tabular-nums text-n-teal-11">
                {{ dig.resumo.valor }}
              </p>
              <div class="flex flex-col gap-1 mt-3 text-xs">
                <span class="flex justify-between text-n-slate-11">
                  Prazo <b class="text-n-slate-12">{{ dig.resumo.prazo }}</b>
                </span>
                <span class="flex justify-between text-n-slate-11">
                  Parcela <b class="text-n-slate-12">{{ dig.resumo.parcela }}</b>
                </span>
                <span v-if="dig.resumo.extra" class="flex justify-between text-n-slate-11">
                  Produto <b class="text-right text-n-slate-12">{{ dig.resumo.extra }}</b>
                </span>
                <span class="flex justify-between text-n-slate-11">
                  Cliente <b class="text-n-slate-12">{{ nome }}</b>
                </span>
                <span class="flex justify-between text-n-slate-11">
                  CPF <b class="tabular-nums text-n-slate-12">{{ cpf }}</b>
                </span>
              </div>
            </div>

            <!-- formulário (v94: borda verde no card inteiro) -->
            <div class="p-4 rounded-xl border-2 border-green-400/70 bg-n-solid-1">
              <h4 class="mb-2 text-sm font-bold tracking-wider uppercase text-n-teal-11">
                Cliente
              </h4>
              <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <label class="text-xs text-n-slate-11">
                  Nome completo
                  <input v-model="form.nome" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                </label>
                <label class="text-xs text-n-slate-11">
                  Nascimento
                  <input v-model="form.nascimento" type="date" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                </label>
                <label class="text-xs text-n-slate-11">
                  E-mail
                  <input v-model="form.email" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                </label>
                <label class="text-xs text-n-slate-11">
                  Telefone
                  <input v-model="form.telefone" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 tabular-nums" />
                </label>
                <label v-if="dig.banco === 'UY3'" class="text-xs text-n-slate-11">
                  Estado civil
                  <select v-model="form.civilStatus" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12">
                    <option value="Single">Solteiro(a)</option>
                    <option value="Married">Casado(a)</option>
                    <option value="Divorced">Divorciado(a)</option>
                    <option value="Widowed">Viúvo(a)</option>
                  </select>
                </label>
                <label v-if="dig.banco === 'UY3'" class="text-xs text-n-slate-11">
                  Profissão
                  <input v-model="form.occupation" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                </label>
                <!-- v125: campos do cliente SOMA -->
                <template v-if="dig.banco === 'SOMA'">
                  <label class="text-xs text-n-slate-11">
                    Sexo
                    <select v-model="form.sexo" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12">
                      <option value="M">Masculino</option>
                      <option value="F">Feminino</option>
                    </select>
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Estado civil
                    <select v-model="form.estadoCivil" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12">
                      <option value="solteiro">Solteiro(a)</option>
                      <option value="casado">Casado(a)</option>
                      <option value="divorciado">Divorciado(a)</option>
                      <option value="viuvo">Viúvo(a)</option>
                      <option value="separado">Separado(a)</option>
                      <option value="uniao">União estável</option>
                    </select>
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Nome da mãe
                    <input v-model="form.nomeMae" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Profissão
                    <input v-model="form.occupation" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                  </label>
                </template>
                <template v-if="dig.banco === 'FACTA'">
                  <label class="text-xs text-n-slate-11">
                    Sexo
                    <select v-model="form.sexo" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12">
                      <option value="M">Masculino</option>
                      <option value="F">Feminino</option>
                    </select>
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Estado civil
                    <select v-model="form.estadoCivil" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12">
                      <option value="solteiro">Solteiro(a)</option>
                      <option value="casado">Casado(a)</option>
                      <option value="divorciado">Divorciado(a)</option>
                      <option value="viuvo">Viúvo(a)</option>
                      <option value="separado">Separado(a)</option>
                      <option value="uniao">União estável</option>
                    </select>
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Nome da mãe
                    <input v-model="form.nomeMae" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                  </label>
                  <label class="text-xs text-n-slate-11">
                    RG
                    <input v-model="form.rg" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Órgão emissor
                    <input v-model="form.rgOrgao" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                  </label>
                  <label class="text-xs text-n-slate-11">
                    UF do RG
                    <input v-model="form.rgUf" maxlength="2" class="px-3 py-2 mt-1 w-full text-sm uppercase rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Expedição (dd/mm/aaaa)
                    <input v-model="form.rgData" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Renda (R$)
                    <input v-model="form.renda" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 tabular-nums" />
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Matrícula
                    <input v-model="form.matricula" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                  </label>
                </template>
              </div>

              <h4 class="mt-4 mb-2 text-sm font-bold tracking-wider uppercase text-n-teal-11">
                Endereço
                <span class="ml-1 font-normal normal-case text-n-slate-11">(preenchido pelo CEP)</span>
              </h4>
              <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <label class="text-xs text-n-slate-11">
                  CEP
                  <div class="flex gap-1.5 mt-1">
                    <input v-model="form.cep" class="px-3 py-2 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 tabular-nums" @blur="buscarCep" />
                    <button
                      type="button"
                      class="px-3 text-xs font-bold text-white rounded-lg bg-n-blue-9 hover:bg-n-blue-10 shrink-0"
                      @click="buscarCep"
                    >
                      Buscar CEP
                    </button>
                  </div>
                </label>
                <label class="text-xs text-n-slate-11">
                  Rua
                  <input v-model="form.rua" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                </label>
                <label class="text-xs text-n-slate-11">
                  Número
                  <input v-model="form.numero" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 tabular-nums" />
                </label>
                <label class="text-xs text-n-slate-11">
                  Complemento
                  <input v-model="form.complemento" placeholder="Apto / bloco (opcional)" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                </label>
                <label class="text-xs text-n-slate-11">
                  Bairro
                  <input v-model="form.bairro" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                </label>
                <label class="text-xs text-n-slate-11">
                  Cidade
                  <input v-model="form.cidade" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                </label>
                <label class="text-xs text-n-slate-11">
                  UF
                  <input v-model="form.uf" maxlength="2" class="px-3 py-2 mt-1 w-full text-sm uppercase rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                </label>
              </div>

              <!-- PIX (UY3/FACTA) ou dados bancários (C6) -->
              <template v-if="dig.banco !== 'C6'">
                <h4 class="mt-4 mb-2 text-sm font-bold tracking-wider uppercase text-n-teal-11">
                  Conta PIX
                </h4>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <label class="text-xs text-n-slate-11">
                    Tipo de chave
                    <select v-model="form.pixTipo" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12">
                      <template v-if="dig.banco === 'UY3'">
                        <option value="NaturalRegistrationNumber">CPF</option>
                        <option value="Phone">Telefone</option>
                        <option value="Email">E-mail</option>
                        <option value="Automatic">Aleatória</option>
                      </template>
                      <!-- v125: enums de PIX do SOMA -->
                      <template v-else-if="dig.banco === 'SOMA'">
                        <option value="CPF">CPF</option>
                        <option value="TELEFONE">Telefone</option>
                        <option value="EMAIL">E-mail</option>
                        <option value="CHAVE_ALEATORIA">Aleatória</option>
                      </template>
                      <!-- enums de PIX da V8 -->
                      <template v-else-if="dig.banco === 'V8'">
                        <option value="cpf">CPF</option>
                        <option value="telephone">Telefone</option>
                        <option value="email">E-mail</option>
                        <option value="random">Aleatória</option>
                      </template>
                      <template v-else>
                        <option value="1">CPF</option>
                        <option value="2">Telefone</option>
                        <option value="3">E-mail</option>
                        <option value="4">Aleatória</option>
                      </template>
                    </select>
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Chave PIX
                    <input v-model="form.pixChave" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 tabular-nums" />
                  </label>
                </div>
              </template>
              <template v-if="dig.banco === 'C6' || dig.banco === 'SOMA'">
                <h4 class="mt-4 mb-2 text-sm font-bold tracking-wider uppercase text-n-teal-11">
                  Dados bancários
                  <span v-if="dig.banco === 'SOMA'" class="ml-1 font-normal normal-case text-n-slate-11">(opcional — use chave PIX OU conta)</span>
                </h4>
                <div class="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <label class="text-xs text-n-slate-11">
                    Tipo de conta
                    <select v-model="form.tipoConta" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12">
                      <template v-if="dig.banco === 'SOMA'">
                        <option value="CONTA_CORRENTE">Conta Corrente</option>
                        <option value="POUPANCA">Poupança</option>
                        <option value="CONTA_PAGAMENTO">Conta Pagamento</option>
                      </template>
                      <option v-else value="ContaCorrenteIndividual">Conta Corrente Individual</option>
                    </select>
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Banco (nº)
                    <input v-model="form.numeroBanco" placeholder="033" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 tabular-nums" />
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Agência
                    <input v-model="form.numeroAgencia" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 tabular-nums" />
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Dígito agência
                    <input v-model="form.digitoAgencia" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 tabular-nums" />
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Conta
                    <input v-model="form.numeroConta" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 tabular-nums" />
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Dígito conta
                    <input v-model="form.digitoConta" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 tabular-nums" />
                  </label>
                </div>
              </template>

              <div class="flex gap-2 items-center pt-3 mt-4 border-t border-n-weak">
                <button
                  type="button"
                  class="px-4 py-2 text-sm font-bold text-white rounded-lg bg-n-teal-9 hover:opacity-90 disabled:opacity-50"
                  :disabled="digBusy"
                  @click="confirmarDigitacao"
                >
                  {{ digBusy ? 'Digitando…' : 'Confirmar digitação' }}
                </button>
                <button
                  type="button"
                  class="px-4 py-2 text-sm font-semibold rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
                  @click="voltarOfertas"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </template>

        <!-- ================= VIEW: RESULTADO ================= -->
        <template v-else-if="view === 'resultado' && resultado">
          <div class="p-6 mx-auto max-w-lg text-center rounded-xl border border-n-teal-7 bg-n-teal-2">
            <span class="inline-block i-lucide-check-circle size-10 text-n-teal-11" />
            <h3 class="mt-2 text-lg font-bold text-n-slate-12">
              Proposta {{ resultado.banco }} digitada!
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">{{ resultado.msg }}</p>
            <p v-if="resultado.numero" class="mt-2 text-xs tabular-nums text-n-slate-11">
              Nº: <b class="text-n-slate-12">{{ resultado.numero }}</b>
            </p>
            <div v-if="resultado.link" class="flex gap-2 justify-center items-center p-2 mt-3 rounded-lg border border-n-weak bg-n-background">
              <span class="text-xs truncate text-n-slate-12">{{ resultado.link }}</span>
              <button
                type="button"
                class="px-3 py-1 text-xs font-bold text-white rounded-lg bg-n-teal-9 hover:opacity-90 shrink-0"
                @click="copiar(resultado.link, 'Link')"
              >
                Copiar
              </button>
            </div>
            <button
              type="button"
              class="px-4 py-2 mt-4 text-sm font-semibold rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
              @click="voltarOfertas"
            >
              ← Voltar às ofertas
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ============================================================================
   v100 — cards de oferta no padrão novo do Kairo (HTML/CSS fornecido por ele,
   convertido pra classes cc-*) + ficha/header com ícones verdes.
   Escopado: NÃO vaza pro resto do Chatwoot.
   ========================================================================= */

/* ---- card de oferta ---- */
.credito-card {
  width: 100%;
  background: linear-gradient(180deg, #17181d, #111216);
  border: 1px solid rgba(30, 211, 190, 0.75);
  border-radius: 16px;
  padding: 16px;
  color: #ffffff;
  box-shadow:
    0 0 0 1px rgba(30, 211, 190, 0.15),
    0 12px 30px rgba(0, 0, 0, 0.35);
}
.credito-card.melhor {
  border-color: #1ee7ca;
  box-shadow:
    0 0 0 2px rgba(30, 231, 202, 0.35),
    0 12px 30px rgba(0, 0, 0, 0.35);
}
.cc-header {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: flex-start;
  flex-wrap: wrap; /* v130: badges quebram linha em vez de estourar */
}
.cc-header > div:first-child {
  min-width: 0; /* v130: permite o valor encolher sem vazar do card */
}
.cc-label {
  font-size: 12px;
  color: #9ca3af;
  display: block;
  margin-bottom: 4px;
}
.cc-valor {
  margin: 0;
  color: #18e0c3;
  font-size: clamp(18px, 1.5vw, 26px); /* v130: encolhe em coluna estreita */
  font-weight: 800;
  letter-spacing: 0.3px;
  overflow-wrap: anywhere;
}
.cc-badges {
  display: flex;
  flex-direction: row; /* v130: em linha, quebrando — não empurra o valor */
  flex-wrap: wrap;
  gap: 4px;
  align-items: flex-start;
  justify-content: flex-end;
}
.cc-badge {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 5px 9px;
  border-radius: 999px;
  white-space: nowrap;
}
.cc-badge.sucesso,
.cc-badge.maxima {
  color: #1ee7ca;
  background: rgba(30, 231, 202, 0.12);
}
.cc-badge.leilao {
  color: #ffc247;
  background: rgba(255, 194, 71, 0.14);
}
/* blocos Prazo/Parcela/Produto — rótulo e valor CENTRALIZADOS (pedido) */
.cc-info {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 16px;
}
.cc-info > div {
  background: #202127;
  border: 1px solid #2b2d35;
  border-radius: 12px;
  padding: 10px;
  text-align: center;
}
.cc-info span {
  display: block;
  font-size: 11px;
  color: #9ca3af;
  margin-bottom: 4px;
}
.cc-info strong {
  font-size: 14px;
  color: #ffffff;
  word-break: break-word;
}
.cc-id {
  margin-top: 10px;
  padding: 11px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  color: #d1d5db;
  font-size: 12px;
  word-break: break-all;
}
.cc-id b {
  color: #ffffff;
}
.cc-actions {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 14px;
}
.cc-btn {
  border: none;
  border-radius: 12px;
  padding: 10px 14px;
  font-weight: 700;
  cursor: pointer;
  color: #ffffff;
  flex: 1;
  font-size: 13px;
  background: #19b8a7;
}
.cc-btn.digitar {
  background: #14a99a;
}
.cc-btn:hover {
  opacity: 0.9;
}
.cc-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.cc-erro {
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #dc2626;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
}
/* ---- caixa de simular ---- */
.cc-simbox {
  margin-top: 14px;
  padding: 13px;
  border-radius: 14px;
  border: 1px dashed rgba(255, 255, 255, 0.12);
  background: #18191f;
}
.cc-simbox > label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #d1d5db;
  margin-bottom: 8px;
}
.cc-toggle {
  display: inline-flex;
  background: #101116;
  border: 1px solid #2b2d35;
  border-radius: 9px;
  padding: 3px;
  margin-bottom: 9px;
}
.cc-toggle button {
  border: none;
  background: transparent;
  color: #9ca3af;
  font-size: 11px;
  font-weight: 700;
  padding: 5px 10px;
  border-radius: 7px;
  cursor: pointer;
}
.cc-toggle button.on {
  background: #18e0c3;
  color: #062b27;
}
.cc-input-area {
  display: flex;
  gap: 8px;
}
.cc-input-area input {
  flex: 1;
  min-width: 0;
  height: 42px;
  border-radius: 10px;
  border: 1px solid #2dd4bf;
  background: #ffffff;
  padding: 0 12px;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  outline: none;
}
.cc-input-area input.prazo {
  flex: 0 0 56px;
  text-align: center;
}
.cc-input-area > button {
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #ffc247, #ffb020);
  color: #111827;
  font-weight: 800;
  padding: 0 16px;
  cursor: pointer;
  font-size: 13px;
  white-space: nowrap;
}
.cc-input-area > button:disabled {
  opacity: 0.5;
  cursor: default;
}
/* ---- header do modal ---- */
.sim-ico {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(30, 231, 202, 0.12);
  color: #1ee7ca;
  border: 1px solid rgba(30, 231, 202, 0.35);
}
/* ---- ficha (CPF/Nome/Nascimento/Celular) ---- */
.ficha-grid {
  display: grid;
  grid-template-columns: 1.1fr 2fr 1fr 1fr;
  gap: 12px;
  padding: 14px;
  border-radius: 12px;
  background: #101116;
  border: 1px solid #23252c;
}
.ficha-campo {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 14px;
  border-left: 1px solid #23252c;
}
.ficha-campo:first-child {
  border-left: none;
  padding-left: 0;
}
@media (max-width: 800px) {
  .ficha-grid {
    grid-template-columns: 1fr 1fr;
  }
  .ficha-campo {
    border-left: none;
    padding-left: 0;
  }
}
.ficha-label {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  font-size: 12px;
  color: #9ca3af;
}
.ficha-label > span {
  color: #1ee7ca;
}
.ficha-input {
  background: transparent;
  border: none;
  outline: none;
  color: #ffffff;
  font-weight: 800;
  font-size: 15px;
  padding: 0;
  width: 100%;
}
.ficha-input::placeholder {
  color: #4b5563;
}
.ficha-input-erro {
  color: #f87171 !important;
}
input[type='date'].ficha-input {
  color-scheme: dark;
}
/* ---- v107: cards de VÍNCULO EMPREGATÍCIO ---- */
.vinc-card {
  text-align: left;
  border-radius: 14px;
  padding: 12px;
  border: 2px solid;
  background: linear-gradient(180deg, #17181d, #111216);
  color: #ffffff;
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.vinc-card.ligado {
  border-color: #1f6feb;
  box-shadow: 0 0 0 1px rgba(31, 111, 235, 0.25);
}
.vinc-card.ligado:hover {
  transform: translateY(-2px);
  box-shadow: 0 0 0 2px rgba(31, 111, 235, 0.45);
}
.vinc-card.desligado {
  border-color: #e5484d;
  box-shadow: 0 0 0 1px rgba(229, 72, 77, 0.25);
  cursor: not-allowed;
  opacity: 0.85;
}
.vinc-card.selecionado {
  border-color: #1ee7ca;
  box-shadow: 0 0 0 3px rgba(30, 231, 202, 0.35);
}
.vinc-topo {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  gap: 8px;
}
.vinc-status {
  font-size: 10px;
  font-weight: 800;
  padding: 3px 8px;
  border-radius: 999px;
  letter-spacing: 0.04em;
}
.ligado .vinc-status {
  color: #79b8ff;
  background: rgba(31, 111, 235, 0.18);
}
.desligado .vinc-status {
  color: #ff9592;
  background: rgba(229, 72, 77, 0.18);
}
.vinc-check {
  display: inline-flex;
  gap: 4px;
  align-items: center;
  font-size: 10px;
  font-weight: 800;
  color: #062b27;
  background: #1ee7ca;
  padding: 3px 8px;
  border-radius: 999px;
}
.vinc-empregador {
  font-size: 14px;
  font-weight: 800;
  word-break: break-word;
}
.vinc-linha {
  margin-top: 2px;
  font-size: 11px;
  color: #9ca3af;
}
.vinc-info {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
  margin-top: 8px;
}
.vinc-info > div {
  background: #202127;
  border: 1px solid #2b2d35;
  border-radius: 10px;
  padding: 7px 9px;
  text-align: center;
}
.vinc-info span {
  display: block;
  font-size: 10px;
  color: #9ca3af;
}
.vinc-info b {
  font-size: 12px;
  color: #ffffff;
}
.vinc-motivo {
  margin-top: 8px;
  font-size: 11px;
  font-weight: 700;
  color: #ff9592;
}
.vinc-fontes {
  margin-top: 6px;
  font-size: 10px;
  color: #6b7280;
  text-align: right;
}

/* ---- botões de ação da ficha ---- */
.acao-btn {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  height: 40px;
  padding: 0 18px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 13px;
}
.acao-buscar {
  background: #16a34a;
}
.acao-buscar:hover {
  background: #15803d;
}
</style>
